import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Users, Film, Crown, Zap, Sparkles, Plus, Trash2, LogOut, ArrowLeft, BarChart3, Award, Tv, Play, Bell, UserPlus, Copy, Edit3, Save, X, Image as ImageIcon, Loader2, Wand2, Building2, Coins, Wallet, Lock, BookOpen, User, Trophy } from "lucide-react";
import CloudinaryUpload from "../components/CloudinaryUpload";
import ImageUploader from "../components/ImageUploader";
import VideoUploader from "../components/VideoUploader";
import ReelThumb from "../components/ReelThumb";
import AdminWalletModal from "../components/AdminWalletModal";
import AdminPayments from "../components/AdminPayments";
import AdminUserRow from "../components/AdminUserRow";
import AdminLiveBoost from "../components/AdminLiveBoost";

const SAMPLES = [
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4",
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
];

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [userQ, setUserQ] = useState("");
  const [walletUserId, setWalletUserId] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [history, setHistory] = useState([]);
  const [msg, setMsg] = useState("");

  // Predict reveal form
  const [predictForm, setPredictForm] = useState({ title: "", reward_text: "", reward_emoji: "🎁", image_url: "", video_url: "", video_public_id: null });
  // Premium profile form
  const [profileForm, setProfileForm] = useState({ name: "", category: "Entertainment", description: "", cover_image: "https://images.pexels.com/photos/8119424/pexels-photo-8119424.jpeg", cover_image_public_id: null, price_goldly: 100 });
  // Premium content form
  const [contentForm, setContentForm] = useState({ profile_id: "", title: "", video_url: SAMPLES[0], thumbnail_url: "", cloudinary_public_id: null, duration_seconds: 60 });

  // Mission form
  const [missionForm, setMissionForm] = useState({ title: "", description: "", icon: "🎯", type: "upload_reels", target: 1, reward_currency: "goldly", reward_amount: 100, active: true });
  const [missionsList, setMissionsList] = useState([]);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") { nav("/"); return; }
    api.get("/admin/stats").then(r => setStats(r.data));
    api.get("/admin/users").then(r => setUsers(r.data));
    api.get("/premium/profiles").then(r => setProfiles(r.data));
    api.get("/predict/history").then(r => setHistory(r.data));
    api.get("/missions").then(r => setMissionsList(r.data));
    api.get("/admin/reports").then(r => setReports(r.data)).catch(()=>{});
  }, [user, nav]);

  const reload = () => {
    api.get("/admin/stats").then(r => setStats(r.data));
    api.get("/admin/users").then(r => setUsers(r.data));
    api.get("/premium/profiles").then(r => setProfiles(r.data));
    api.get("/predict/history").then(r => setHistory(r.data));
    api.get("/missions").then(r => setMissionsList(r.data));
    api.get("/admin/reports").then(r => setReports(r.data)).catch(()=>{});
  };

  const createMission = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await api.post("/missions", missionForm);
      setMsg("Mission created!");
      setMissionForm({ title: "", description: "", icon: "🎯", type: "upload_reels", target: 1, reward_currency: "goldly", reward_amount: 100, active: true });
      reload();
    } catch (e) { setMsg(formatErr(e)); }
  };

  const deleteMission = async (id) => {
    if (!window.confirm("Delete mission?")) return;
    await api.delete(`/missions/${id}`); reload();
  };

  const patchUser = (uid, patch) => setUsers(list => list.map(x => x.id === uid ? { ...x, ...patch } : x));
  const toggleVerify = useCallback(async (u) => {
    patchUser(u.id, { verified: !u.verified });
    try { const { data } = await api.patch(`/admin/users/${u.id}/verify`); patchUser(u.id, { verified: data.verified }); }
    catch (e) { patchUser(u.id, { verified: u.verified }); alert(formatErr(e)); }
  }, []);
  const [shown, setShown] = useState(50);
  useEffect(() => {
    if (tab !== "users") return undefined;
    const t = setTimeout(() => {
      api.get("/admin/users", { params: userQ.trim() ? { q: userQ.trim() } : {} }).then(r => { setUsers(r.data); setShown(50); }).catch(() => {});
    }, userQ ? 300 : 0);
    return () => clearTimeout(t);
  }, [userQ, tab]);
  const visibleUsers = useMemo(() => users.slice(0, shown), [users, shown]);
  const restoreUser = useCallback(async (u) => {
    if (!window.confirm(`Restore @${u.username}'s account?`)) return;
    try { await api.post(`/admin/users/${u.id}/restore`); patchUser(u.id, { deleted: false }); } catch (e) { alert(formatErr(e)); }
  }, []);

  const doReveal = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await api.post("/predict/reveal", predictForm);
      setMsg("Predict & Enjoy revealed to all users!");
      setPredictForm({ title: "", reward_text: "", reward_emoji: "🎁", image_url: "", video_url: "", video_public_id: null });
      reload();
    } catch (e) { setMsg(formatErr(e)); }
  };

  const createProfile = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await api.post("/premium/profiles", profileForm);
      setMsg("Premium profile created!");
      setProfileForm({ name: "", category: "Entertainment", description: "", cover_image: "https://images.pexels.com/photos/8119424/pexels-photo-8119424.jpeg", cover_image_public_id: null, price_goldly: 100 });
      reload();
    } catch (e) { setMsg(formatErr(e)); }
  };

  const delProfile = async (id) => {
    if (!window.confirm("Delete this premium profile and all its content?")) return;
    await api.delete(`/premium/profiles/${id}`); reload();
  };

  const addContent = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await api.post("/premium/content", contentForm);
      setMsg("Content added to profile!");
      setContentForm({ profile_id: contentForm.profile_id, title: "", video_url: SAMPLES[0], thumbnail_url: "", duration_seconds: 60 });
      reload();
    } catch (e) { setMsg(formatErr(e)); }
  };

  const delUser = useCallback(async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); setUsers(list => list.filter(x => x.id !== id)); } catch (e) { alert(formatErr(e)); }
  }, []);

  // Admin → search any user → view all their reels → delete violating content
  const [userReelsModal, setUserReelsModal] = useState(null); // {user, reels: []}
  const openUserReels = useCallback(async (u) => {
    setUserReelsModal({ user: u, reels: [], loading: true });
    try {
      const { data } = await api.get(`/users/${u.id}`);
      setUserReelsModal(m => m && m.user.id === u.id ? { user: u, reels: data?.reels || [], loading: false } : m);
    } catch (e) { setUserReelsModal(null); window.alert("Could not load reels: " + formatErr(e)); }
  }, []);
  const adminDeleteReel = async (reel) => {
    if (!window.confirm(`Delete reel "${reel.title || 'Untitled'}" permanently? This is for violating content takedowns.`)) return;
    try {
      await api.delete(`/reels/${reel.id}`);
      setUserReelsModal(m => m ? { ...m, reels: m.reels.filter(r => r.id !== reel.id) } : null);
    } catch (e) { window.alert("Delete failed: " + formatErr(e)); }
  };

  const TABS = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "payments", label: "Payments", icon: Wallet },
    { id: "revenue", label: "Revenue Wallet", icon: Wallet },
    { id: "applications", label: "Applications", icon: Building2 },
    { id: "boost", label: "Boost Reels", icon: Zap },
    { id: "live-boost", label: "Boost Live Viewers", icon: Tv },
    { id: "locked-pricing", label: "Locked Rooms", icon: Lock },
    { id: "biography", label: "Biography", icon: BookOpen },
    { id: "artists", label: "Book Artist", icon: User },
    { id: "drama", label: "Drama Series", icon: Tv },
    { id: "leads", label: "Lead Requests", icon: Users },
    { id: "predict", label: "Predict & Enjoy", icon: Sparkles },
    { id: "spin", label: "Spin Wheel", icon: Trophy },
    { id: "premium", label: "Premium", icon: Crown },
    { id: "missions", label: "Missions", icon: Award },
    { id: "users", label: "Users", icon: Users },
    { id: "reports", label: "Reports", icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back"><ArrowLeft className="w-5 h-5"/></Link>
            <div>
              <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Admin Panel</h1>
              <p className="text-xs text-white/60">Logged in as <b className="text-[#FF7A00]">{user?.email}</b></p>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm flex items-center gap-1"><LogOut className="w-4 h-4"/>Logout</button>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide">
          {TABS.map(t => (
            <button key={t.id} onClick={()=>setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex-shrink-0 px-4 py-2 rounded-full border text-sm font-semibold flex items-center gap-1.5 ${tab===t.id?"border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]":"border-white/10 bg-white/5"}`}>
              <t.icon className="w-4 h-4"/>{t.label}
            </button>
          ))}
        </div>

        {msg && <div className="mb-4 p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm" data-testid="admin-msg">{msg}</div>}

        {tab === "dashboard" && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
            {[
              { label: "Total Users", value: stats.users, icon: Users, color: "text-[#00E5FF]" },
              { label: "Total Reels", value: stats.reels, icon: Film, color: "text-[#FF2A5F]" },
              { label: "Premium Profiles", value: stats.premium_profiles, icon: Crown, color: "text-[#FF7A00]" },
              { label: "Premium Content", value: stats.premium_content, icon: Crown, color: "text-[#FF7A00]" },
              { label: "Active Boosts", value: stats.active_boosts, icon: Zap, color: "text-[#00E5FF]" },
              { label: "Transactions", value: stats.transactions, icon: BarChart3, color: "text-emerald-400" },
              { label: "Predict Reveals", value: stats.predict_reveals, icon: Sparkles, color: "text-[#FF2A5F]" },
            ].map(s => (
              <div key={s.label} className="p-5 rounded-2xl bg-white/5 border border-white/10">
                <s.icon className={`w-7 h-7 ${s.color} mb-2`}/>
                <div className="font-outfit font-extrabold text-3xl">{s.value ?? 0}</div>
                <div className="text-xs text-white/60 uppercase tracking-wide mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "dashboard" && <SoraUsageTile/>}

        {tab === "drama" && <DramaAdminPanel />}
        {tab === "leads" && (
          <div className="space-y-4">
            <LeadGenConfigPanel/>
            <LeadsAdminPanel/>
          </div>
        )}
        {tab === "broadcast" && <BroadcastAdminPanel setMsg={setMsg} />}

        {tab === "predict" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <PredictAdminPanel />
          </div>
        )}

        {tab === "premium" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="lg:col-span-2 -mb-2 p-3 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/40 text-xs text-white/85">
              ⚠️ <b className="text-[#FF7A00]">IMPORTANT:</b> Use this form (or <Link to="/admin/premium-content" className="underline text-[#00E5FF]">Premium Content Admin</Link>) to add premium videos. Do NOT use the regular &quot;+&quot; Upload button in the bottom nav — that publishes to your personal reel feed, not Premium. Manage store items & prices in <Link to="/admin/store" className="underline text-[#FF7A00]" data-testid="admin-store-link">Store Editor</Link>.
            </div>
            <form onSubmit={createProfile} className="p-5 rounded-2xl bg-white/5 border border-white/10 space-y-3" data-testid="profile-form">
              <h2 className="font-outfit font-bold text-xl mb-2"><Plus className="inline w-5 h-5"/> Create Premium Profile</h2>
              <input required placeholder="Profile name" value={profileForm.name} onChange={e=>setProfileForm(f=>({...f, name:e.target.value}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none" data-testid="profile-name"/>
              <input required placeholder="Category" value={profileForm.category} onChange={e=>setProfileForm(f=>({...f, category:e.target.value}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none" data-testid="profile-cat"/>
              <textarea placeholder="Description" value={profileForm.description} onChange={e=>setProfileForm(f=>({...f, description:e.target.value}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none" data-testid="profile-desc"/>
              <input placeholder="Cover image URL" value={profileForm.cover_image} onChange={e=>setProfileForm(f=>({...f, cover_image:e.target.value, cover_image_public_id:null}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
              <div className="text-[10px] text-white/50 -mt-1">— or upload image —</div>
              <CloudinaryUpload kind="image" maxMB={10} testId="profile-cover-upload"
                currentUrl={profileForm.cover_image_public_id ? profileForm.cover_image : null}
                onUploaded={(d) => setProfileForm(f => ({...f, cover_image: d.secure_url, cover_image_public_id: d.public_id}))} />
              <div className="flex items-center gap-2">
                <label className="text-xs text-white/60">Unlock cost (Goldly):</label>
                <input type="number" min={0} value={profileForm.price_goldly} onChange={e=>setProfileForm(f=>({...f, price_goldly:parseInt(e.target.value)||0}))} className="w-32 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none" data-testid="profile-price"/>
              </div>
              <button data-testid="profile-submit" className="w-full py-3 rounded-full bg-[#FF7A00] text-black font-bold">Create Profile</button>

              <hr className="border-white/10 my-4"/>
              <h3 className="font-outfit font-bold">Add Content to Profile</h3>
              <select required value={contentForm.profile_id} onChange={e=>setContentForm(f=>({...f, profile_id:e.target.value}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none" data-testid="content-profile">
                <option value="">-- Select profile --</option>
                {profiles.map(p => <option key={p.id} value={p.id} className="bg-[#0B0F19]">{p.name}</option>)}
              </select>
              <input required placeholder="Content title" value={contentForm.title} onChange={e=>setContentForm(f=>({...f, title:e.target.value}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none" data-testid="content-title"/>
              <input required placeholder="Video URL (or upload below)" value={contentForm.video_url} onChange={e=>setContentForm(f=>({...f, video_url:e.target.value, cloudinary_public_id:null}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
              <div className="text-[10px] text-white/50 -mt-1">— or upload video —</div>
              <CloudinaryUpload kind="video" maxMB={500} testId="content-video-upload"
                currentUrl={contentForm.cloudinary_public_id ? contentForm.video_url : null}
                onUploaded={(d) => setContentForm(f => ({
                  ...f,
                  video_url: d.secure_url,
                  cloudinary_public_id: d.public_id,
                  duration_seconds: d.duration ? Math.round(d.duration) : f.duration_seconds,
                }))} />
              <input type="number" placeholder="Duration (seconds)" value={contentForm.duration_seconds} onChange={e=>setContentForm(f=>({...f, duration_seconds:parseInt(e.target.value)||60}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
              <button onClick={addContent} type="button" data-testid="content-submit" className="w-full py-3 rounded-full bg-[#00E5FF] text-black font-bold">Add Content</button>
            </form>

            <div className="space-y-3" data-testid="profile-list">
              <h2 className="font-outfit font-bold text-xl">Existing Profiles ({profiles.length})</h2>
              {profiles.map(p => (
                <div key={p.id} className="p-4 rounded-2xl bg-white/5 border border-white/10 flex gap-3">
                  <img src={p.cover_image} alt="" className="w-16 h-16 rounded-xl object-cover"/>
                  <div className="flex-1">
                    <div className="font-bold">{p.name} <span className="text-xs text-white/50">• {p.category}</span></div>
                    <div className="text-xs text-white/60">{p.description}</div>
                    <div className="text-xs text-[#FF7A00] mt-1">{p.content_count} videos • {p.price_goldly} Goldly</div>
                  </div>
                  <button onClick={()=>delProfile(p.id)} data-testid={`del-profile-${p.id}`} className="text-[#FF2A5F]"><Trash2 className="w-5 h-5"/></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "missions" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={createMission} className="p-5 rounded-2xl bg-white/5 border border-white/10 space-y-3" data-testid="mission-form">
              <h2 className="font-outfit font-bold text-xl mb-2">🎯 Create Mission</h2>
              <input required placeholder="Title" value={missionForm.title} onChange={e=>setMissionForm(f=>({...f, title:e.target.value}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none" data-testid="mform-title"/>
              <input placeholder="Icon (emoji)" value={missionForm.icon} onChange={e=>setMissionForm(f=>({...f, icon:e.target.value}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
              <textarea required placeholder="Description" value={missionForm.description} onChange={e=>setMissionForm(f=>({...f, description:e.target.value}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
              <select value={missionForm.type} onChange={e=>setMissionForm(f=>({...f, type:e.target.value}))} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none" data-testid="mform-type">
                {["upload_reels","send_gifts","go_live_minutes","get_likes","get_followers","share_profile","recharge","watch_live"].map(t => <option key={t} value={t} className="bg-[#0B0F19]">{t}</option>)}
              </select>
              <div className="grid grid-cols-3 gap-2">
                <input type="number" min={1} placeholder="Target" value={missionForm.target} onChange={e=>setMissionForm(f=>({...f, target:parseInt(e.target.value)||1}))} className="px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none" data-testid="mform-target"/>
                <select value={missionForm.reward_currency} onChange={e=>setMissionForm(f=>({...f, reward_currency:e.target.value}))} className="px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none">
                  <option value="goldly" className="bg-[#0B0F19]">Goldly</option>
                  <option value="tokwin" className="bg-[#0B0F19]">Tokwin</option>
                </select>
                <input type="number" min={1} placeholder="Reward" value={missionForm.reward_amount} onChange={e=>setMissionForm(f=>({...f, reward_amount:parseInt(e.target.value)||1}))} className="px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
              </div>
              <button data-testid="mform-submit" className="w-full py-3 rounded-full bg-[#00E5FF] text-black font-bold">Create Mission</button>
            </form>
            <div className="space-y-2">
              <h2 className="font-outfit font-bold text-xl">All Missions ({missionsList.length})</h2>
              {missionsList.map(m => (
                <div key={m.id} className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3" data-testid={`adm-mission-${m.id}`}>
                  <div className="text-2xl">{m.icon}</div>
                  <div className="flex-1 text-sm">
                    <div className="font-bold">{m.title}</div>
                    <div className="text-xs text-white/60">{m.description}</div>
                    <div className="text-[10px] text-white/50 mt-0.5">Target: {m.target} • Reward: {m.reward_amount} {m.reward_currency}</div>
                  </div>
                  <button onClick={()=>deleteMission(m.id)} data-testid={`del-mission-${m.id}`} className="text-[#FF2A5F]"><Trash2 className="w-5 h-5"/></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "applications" && <ApplicationsPanel/>}
        {tab === "revenue" && <RevenueWalletPanel/>}
        {tab === "spin" && <SpinInsightsPanel/>}
        {tab === "boost" && <BoostReelsPanel/>}
        {tab === "live-boost" && <AdminLiveBoost/>}
        {tab === "locked-pricing" && <LockedRoomsPricingPanel/>}
        {tab === "biography" && <BiographyAdminLink/>}
        {tab === "artists" && <ArtistsAdminPanel/>}

        {tab === "payments" && <AdminPayments/>}

        {tab === "reports" && (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <h2 className="font-outfit font-bold text-xl mb-3">User Reports ({reports.length})</h2>
            {reports.length === 0 ? <p className="text-white/50 text-sm">No reports yet.</p> : (
              <div className="space-y-2" data-testid="reports-list">
                {reports.map(r => <AdminReportRow key={r.id} r={r} onChange={reload}/>)}
              </div>
            )}
          </div>
        )}

        {tab === "users" && (
          <div className="space-y-4">
            <SeedCreatorsPanel onSeeded={reload}/>
            <AdminCreateUserForm onCreated={reload}/>
            <CreditWalletForm onCredited={reload} />
            <div className="flex items-center gap-2">
              <input value={userQ} onChange={e=>setUserQ(e.target.value)} placeholder="Search users by name, username, email or phone" data-testid="admin-user-search"
                className="flex-1 px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF] text-sm"/>
              <span className="text-xs text-white/50" data-testid="admin-user-count">{users.length}{users.length >= 1000 ? "+" : ""} users</span>
              <a href="/Hifireels_Creator_Accounts_201_400.pdf" target="_blank" rel="noreferrer" data-testid="admin-creators-pdf" className="px-3 py-2 rounded-xl bg-white/10 text-[11px] font-bold whitespace-nowrap">201–400 PDF</a>
              <a href="/Hifireels_Creator_Accounts_401_3000.pdf" target="_blank" rel="noreferrer" data-testid="admin-creators-pdf-3000" className="px-3 py-2 rounded-xl bg-white/10 text-[11px] font-bold whitespace-nowrap">401–3000 PDF</a>
              <button onClick={async()=>{ try { const { data } = await api.post("/admin/email-seed-accounts"); alert(`Sent ${data.count} accounts to ${data.sent_to}`); } catch (e) { alert(formatErr(e)); } }} data-testid="admin-creators-email" className="px-3 py-2 rounded-xl bg-[#FFD700]/15 text-[#FFD700] text-[11px] font-bold whitespace-nowrap">Email list</button>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-3">User</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Phone</th>
                  <th className="text-left p-3">Role</th>
                  <th className="text-left p-3">Wallet</th>
                  <th className="text-left p-3">Streams</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody data-testid="users-list">
                {visibleUsers.map(u => (
                  <AdminUserRow key={u.id} u={u} onWallet={setWalletUserId} onReels={openUserReels} onVerify={toggleVerify} onDelete={delUser} onRestore={restoreUser}/>
                ))}
              </tbody>
            </table>
            </div>
            {users.length > shown && (
              <button type="button" onClick={() => setShown(n => n + 50)} data-testid="admin-users-show-more" className="w-full py-3 rounded-2xl bg-white/5 border border-white/10 text-sm font-bold active:scale-[0.99]">
                Show more ({users.length - shown} left)
              </button>
            )}

            {walletUserId && <AdminWalletModal userId={walletUserId} onClose={()=>setWalletUserId(null)}/>}
            {/* Admin reels viewer / takedown modal */}
            {userReelsModal && (
              <div className="fixed inset-0 z-[80] bg-black/75 backdrop-blur flex items-center justify-center p-4" onClick={() => setUserReelsModal(null)} data-testid="admin-user-reels-modal">
                <div onClick={e => e.stopPropagation()} className="w-full max-w-2xl max-h-[88vh] flex flex-col rounded-3xl bg-[#0B0F19] border border-white/15 overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
                    <img src={userReelsModal.user.avatar} className="w-9 h-9 rounded-full"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-outfit font-bold truncate">{userReelsModal.user.name || userReelsModal.user.username}</div>
                      <div className="text-[11px] text-white/55 truncate">@{userReelsModal.user.username || userReelsModal.user.name} · {userReelsModal.reels.length} reels</div>
                    </div>
                    <button onClick={() => setUserReelsModal(null)} data-testid="admin-user-reels-close" className="p-2 rounded-full bg-white/5"><X className="w-4 h-4 text-white/70"/></button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3"><div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {userReelsModal.loading && <div className="col-span-full p-8 flex justify-center" data-testid="admin-user-reels-loading"><Loader2 className="w-6 h-6 animate-spin"/></div>}
                    {!userReelsModal.loading && userReelsModal.reels.length === 0 && (
                      <div className="col-span-full p-8 text-center text-white/50 text-sm">No reels uploaded yet.</div>
                    )}
                    {userReelsModal.reels.map(reel => (
                      <div key={reel.id} data-testid={`admin-user-reel-${reel.id}`} className="relative rounded-xl bg-black border border-white/10 overflow-hidden aspect-[3/4]">
                        <ReelThumb reel={reel} className="!absolute inset-0 w-full h-full !rounded-none !border-0" iconSize="w-6 h-6"/>
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/85"/>
                        <div className="absolute bottom-2 left-2 right-2 text-[10px] text-white/85 line-clamp-2 font-bold">{reel.title || reel.description?.slice(0,40) || "Untitled"}</div>
                        <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-black/60 text-[9px] font-bold">👁 {reel.views || 0}</div>
                        <button onClick={() => adminDeleteReel(reel)} data-testid={`admin-del-reel-${reel.id}`}
                          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-[#FF2A5F] text-white flex items-center justify-center active:scale-90 shadow-lg">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                    ))}
                  </div></div>
                  <div className="px-4 py-3 border-t border-white/10 text-[10px] text-white/50 text-center">
                    Use this only for violating content. Deletions are permanent.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ApplicationsPanel() {
  const [tab, setTab] = useState("agency");
  const [agency, setAgency] = useState([]);
  const [agent, setAgent] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/applications/agency").then(r => r.data || []).catch(() => []),
      api.get("/admin/applications/agent").then(r => r.data || []).catch(() => []),
    ]).then(([a, b]) => { setAgency(a); setAgent(b); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const list = tab === "agency" ? agency : agent;

  return (
    <div className="space-y-4" data-testid="admin-applications">
      <div className="flex items-center gap-2">
        <button onClick={() => setTab("agency")} data-testid="apps-tab-agency"
          className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 border ${tab==="agency" ? "border-[#FF7A00] bg-[#FF7A00]/10 text-[#FF7A00]" : "border-white/10 bg-white/5"}`}>
          <Building2 className="w-4 h-4"/>Agency ({agency.length})
        </button>
        <button onClick={() => setTab("agent")} data-testid="apps-tab-agent"
          className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 border ${tab==="agent" ? "border-[#FF7A00] bg-[#FF7A00]/10 text-[#FF7A00]" : "border-white/10 bg-white/5"}`}>
          <Coins className="w-4 h-4"/>Coin Seller / Agent ({agent.length})
        </button>
        <button onClick={load} className="ml-auto px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs">↻ Refresh</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]"/></div>
      ) : list.length === 0 ? (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-8 text-center text-white/60 text-sm">
          No {tab === "agency" ? "agency" : "coin-seller / agent"} applications yet.
        </div>
      ) : (
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid={`apps-table-${tab}`}>
            <thead className="bg-white/5">
              <tr className="text-left">
                <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">Date</th>
                <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">Name</th>
                <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">Username</th>
                <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">{tab === "agency" ? "Agency" : "Agent Name"}</th>
                <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">Email</th>
                {tab === "agency" && <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">Creators</th>}
                <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.map(r => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/3" data-testid={`apps-row-${r.id}`}>
                  <td className="px-3 py-2 text-[11px] text-white/70 whitespace-nowrap">{new Date(r.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                  <td className="px-3 py-2 font-semibold">{r.first_name} {r.last_name}</td>
                  <td className="px-3 py-2 text-white/70">@{r.username || "—"}</td>
                  <td className="px-3 py-2">{tab === "agency" ? r.agency_name : r.agent_name}</td>
                  <td className="px-3 py-2"><a href={`mailto:${r.email}`} className="text-[#00E5FF] hover:underline">{r.email}</a></td>
                  {tab === "agency" && <td className="px-3 py-2 text-white/80">{r.creator_count}</td>}
                  <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[10px] font-bold uppercase">{r.status || "new"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function SpinInsightsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/spin/insights").then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]"/></div>;
  if (!data) return <div className="text-white/60">Failed to load spin insights.</div>;

  const StatCard = ({ label, value, sub, testId, tone }) => (
    <div className={`p-3 rounded-2xl border ${tone || "bg-white/5 border-white/10"}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">{label}</div>
      <div className="font-outfit font-black text-2xl mt-1">{(value || 0).toLocaleString()}</div>
      {sub && <div className="text-[10px] text-white/45 mt-0.5">{sub}</div>}
    </div>
  );

  const Bucket = ({ title, d, testId }) => (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid={testId}>
      <div className="font-outfit font-extrabold text-lg mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-2">
        <StatCard label="Spins" value={d.spins} testId={`${testId}-spins`}/>
        <StatCard label="Free spins used" value={d.free_spins_used} testId={`${testId}-free`}/>
        <StatCard label="Goldly deducted" value={d.goldly_deducted} sub="from users" testId={`${testId}-deducted`}
          tone="bg-gradient-to-br from-[#FF7A00]/15 to-transparent border-[#FF7A00]/30"/>
        <StatCard label="Goldly paid out" value={d.goldly_paid_out} sub="prizes credited" testId={`${testId}-payout`}
          tone="bg-gradient-to-br from-[#FF2A5F]/15 to-transparent border-[#FF2A5F]/30"/>
        <StatCard label="Net revenue" value={d.net_revenue} sub="deducted − paid out" testId={`${testId}-net`}
          tone={d.net_revenue >= 0 ? "bg-gradient-to-br from-emerald-500/15 to-transparent border-emerald-500/30" : "bg-gradient-to-br from-red-500/15 to-transparent border-red-500/30"}/>
        <StatCard label="Jackpots" value={d.jackpots} sub="100K+ wins" testId={`${testId}-jackpots`}
          tone="bg-gradient-to-br from-[#FBBF24]/20 to-transparent border-[#FBBF24]/40"/>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-spin-insights">
      <Bucket title="Today" d={data.today} testId="spin-today"/>
      <Bucket title="Last 7 days" d={data.week} testId="spin-week"/>
      <Bucket title="Last 30 days" d={data.month} testId="spin-month"/>

      <div className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="spin-jackpot-ledger">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-4 h-4 text-[#FBBF24]"/>
          <div className="font-outfit font-extrabold text-lg">Jackpot payout ledger</div>
          <span className="ml-auto text-[10px] text-white/45">Last 30 days · {data.jackpot_ledger.length} payouts</span>
        </div>
        {data.jackpot_ledger.length === 0 ? (
          <div className="text-sm text-white/50 py-4 text-center">No jackpots paid out in the last 30 days.</div>
        ) : (
          <div className="space-y-1.5">
            {data.jackpot_ledger.map((j, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-xl bg-gradient-to-r from-[#FBBF24]/10 to-transparent border border-[#FBBF24]/20" data-testid={`spin-jackpot-entry-${i}`}>
                <Trophy className="w-4 h-4 text-[#FBBF24] flex-shrink-0"/>
                <div className="flex-1 min-w-0 text-sm">
                  <div className="font-bold truncate">@{j.user_username || "user"}</div>
                  <div className="text-[10px] text-white/45 truncate">{j.user_email}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-outfit font-black text-[#FBBF24]">+{j.prize.toLocaleString()}</div>
                  <div className="text-[10px] text-white/45">{new Date(j.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RevenueWalletPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/revenue").then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]"/></div>;
  if (!data) return <div className="text-white/60">Failed to load revenue data.</div>;

  return (
    <div className="space-y-4" data-testid="admin-revenue">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 rounded-2xl bg-gradient-to-br from-[#FF7A00]/15 to-[#FF2A5F]/10 border border-[#FF7A00]/30" data-testid="revenue-total">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Total Goldly Collected</div>
          <div className="font-outfit font-black text-3xl text-[#FF7A00] mt-1 flex items-center gap-1"><Coins className="w-6 h-6"/>{(data.total_goldly || 0).toLocaleString()}</div>
        </div>
        <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Total Transactions</div>
          <div className="font-outfit font-black text-3xl mt-1">{(data.total_txns || 0).toLocaleString()}</div>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-black mb-2">By Service</div>
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5"><tr className="text-left">
              <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">Service</th>
              <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider text-right">Transactions</th>
              <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider text-right">Goldly</th>
            </tr></thead>
            <tbody>
              {(data.by_service || []).map(r => (
                <tr key={r.service} className="border-t border-white/5" data-testid={`revenue-svc-${r.service}`}>
                  <td className="px-3 py-2 font-semibold">{r.service}</td>
                  <td className="px-3 py-2 text-right text-white/80">{r.count.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-[#FF7A00] font-bold">{r.total.toLocaleString()}</td>
                </tr>
              ))}
              {(data.by_service || []).length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-white/50 text-center text-xs">No revenue yet — first paid transaction will appear here.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-black mb-2">Top Spenders (per user)</div>
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5"><tr className="text-left">
              <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">User</th>
              <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">Email</th>
              <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider text-right">Txns</th>
              <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider text-right">Total Goldly</th>
            </tr></thead>
            <tbody>
              {(data.by_user || []).map((u, i) => (
                <tr key={u.user_id || i} className="border-t border-white/5" data-testid={`revenue-user-${u.user_id}`}>
                  <td className="px-3 py-2 font-semibold">@{u.user_username || "—"}</td>
                  <td className="px-3 py-2 text-white/70">{u.user_email}</td>
                  <td className="px-3 py-2 text-right">{(u.count || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-[#FF7A00] font-bold">{(u.total || 0).toLocaleString()}</td>
                </tr>
              ))}
              {(data.by_user || []).length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-white/50 text-center text-xs">No user has purchased yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-black mb-2">Recent Transactions (last {(data.recent || []).length})</div>
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 sticky top-0"><tr className="text-left">
              <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">Time</th>
              <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">User</th>
              <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider">Service</th>
              <th className="px-3 py-2 text-white/60 text-[11px] uppercase tracking-wider text-right">Goldly</th>
            </tr></thead>
            <tbody>
              {(data.recent || []).map(t => (
                <tr key={t.id} className="border-t border-white/5">
                  <td className="px-3 py-2 text-[11px] whitespace-nowrap">{new Date(t.created_at).toLocaleString(undefined, { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" })}</td>
                  <td className="px-3 py-2 text-white/85">@{t.user_username || t.user_email}</td>
                  <td className="px-3 py-2 text-white/70 text-[11px]">{t.service}{t.service_detail ? ` · ${t.service_detail.slice(0,60)}` : ""}</td>
                  <td className="px-3 py-2 text-right text-[#FF7A00] font-bold">{(t.goldly_amount || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <button onClick={load} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs">↻ Refresh</button>
    </div>
  );
}

function BoostReelsPanel() {
  const [q, setQ] = useState("");
  const [reels, setReels] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState("");
  const [values, setValues] = useState({}); // { [reelId]: {likes, views, comments} }

  const search = async () => {
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/reels/search?q=${encodeURIComponent(q.trim())}`);
      setReels(data || []);
    } catch { setReels([]); }
    finally { setSearching(false); }
  };

  useEffect(() => { search(); }, []); // initial: recent reels

  const upd = (id, field, v) => setValues(V => ({ ...V, [id]: { ...(V[id] || {}), [field]: v } }));

  const boost = async (r) => {
    const v = values[r.id] || {};
    const likes = Number(v.likes || 0);
    const views = Number(v.views || 0);
    const comments = Number(v.comments || 0);
    if (!likes && !views && !comments) { setMsg("Enter at least one value"); return; }
    setBusyId(r.id); setMsg("");
    try {
      const { data } = await api.post("/admin/reels/boost", { reel_id: r.id, likes, views, comments });
      setMsg(`Boosted @${r.user?.username || "reel"}: +${data.added.likes} likes, +${data.added.views} views, +${data.added.comments} comments ✓`);
      setReels(rs => rs.map(x => x.id === r.id ? data.reel : x));
      setValues(V => ({ ...V, [r.id]: { likes: "", views: "", comments: "" } }));
    } catch (e) { setMsg(e.response?.data?.detail || e.message); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-3" data-testid="admin-boost">
      <p className="text-white/70 text-xs">Add likes / views / comments to any reel (real user OR admin/demo). Real user data is not real spend — this is a manual promotional boost.</p>
      <div className="flex gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by username or caption keyword…"
          onKeyDown={e => e.key === "Enter" && search()} data-testid="boost-search-input"
          className="flex-1 px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00] text-sm"/>
        <button onClick={search} disabled={searching} data-testid="boost-search-btn"
          className="px-4 py-2.5 rounded-2xl bg-[#FF7A00] text-black font-bold text-sm active:scale-95 disabled:opacity-50 flex items-center gap-1">
          {searching ? <Loader2 className="w-4 h-4 animate-spin"/> : "🔍 Search"}
        </button>
      </div>
      {msg && <div className="p-2 rounded-xl bg-white/5 border border-white/10 text-xs text-white/80" data-testid="boost-msg">{msg}</div>}
      {reels.length === 0 ? (
        <div className="text-center py-8 text-white/50 text-sm">No reels found. Try a different keyword or leave blank to see recent reels.</div>
      ) : (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {reels.map(r => (
            <BoostReelRow key={r.id} r={r} values={values} upd={upd} boost={boost} busyId={busyId}/>
          ))}
        </div>
      )}
    </div>
  );
}

// Admin boost row — isolated so each row has its own thumbnail state.
// Uses <ReelThumb> which falls back from thumbnail_url → first frame of
// video_url → Film icon, so no reel ever shows a blank gray box.
function BoostReelRow({ r, values, upd, boost, busyId }) {
  const v = values[r.id] || {};
  return (
    <div className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3" data-testid={`boost-row-${r.id}`}>
      <ReelThumb reel={r} className="w-14 h-20"/>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold truncate">@{r.user?.username || "unknown"}</div>
        <div className="text-[11px] text-white/60 truncate">{r.caption || r.description || <em>No caption</em>}</div>
        <div className="text-[10px] text-white/50 mt-0.5">Current: {r.likes || 0} likes · {r.views || 0} views · {r.comments_count || 0} comments</div>
        <div className="grid grid-cols-3 gap-1 mt-1.5">
          <input type="number" min={0} placeholder="+ Likes" value={v.likes || ""} onChange={e => upd(r.id, "likes", e.target.value)}
            data-testid={`boost-likes-${r.id}`} className="px-2 py-1 rounded-lg bg-white/5 border border-white/10 outline-none text-xs w-full"/>
          <input type="number" min={0} placeholder="+ Views" value={v.views || ""} onChange={e => upd(r.id, "views", e.target.value)}
            data-testid={`boost-views-${r.id}`} className="px-2 py-1 rounded-lg bg-white/5 border border-white/10 outline-none text-xs w-full"/>
          <input type="number" min={0} placeholder="+ Comments" value={v.comments || ""} onChange={e => upd(r.id, "comments", e.target.value)}
            data-testid={`boost-comments-${r.id}`} className="px-2 py-1 rounded-lg bg-white/5 border border-white/10 outline-none text-xs w-full"/>
        </div>
      </div>
      <button onClick={() => boost(r)} disabled={busyId === r.id} data-testid={`boost-apply-${r.id}`}
        className="px-3 py-2 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-outfit font-black text-xs flex-shrink-0 flex items-center gap-1 active:scale-95 disabled:opacity-50">
        {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Zap className="w-3.5 h-3.5"/>}
        Boost
      </button>
    </div>
  );
}


function LockedRoomsPricingPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCat, setSavingCat] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => { setLoading(true); api.get("/admin/locked-pricing").then(r => setRows(r.data || [])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async (row) => {
    setErr(""); setMsg(""); setSavingCat(row.category);
    try {
      await api.put("/admin/locked-pricing", {
        category: row.category, cost: Number(row.cost), hours: Number(row.hours) || 1,
        host_share_pct: Number(row.host_share_pct) || 70, label: row.label,
      });
      setMsg(`${row.label || row.category} updated ✓`);
      load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setSavingCat(null); }
  };

  const upd = (cat, field, val) => setRows(rs => rs.map(r => r.category === cat ? { ...r, [field]: val } : r));

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]"/></div>;

  return (
    <div className="space-y-3" data-testid="admin-locked-pricing">
      <p className="text-white/70 text-xs">Set unlock cost + host share for each locked live-stream category. Changes take effect immediately.</p>
      {msg && <div className="p-2 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs">{msg}</div>}
      {err && <div className="p-2 rounded-2xl bg-[#FF2A5F]/15 border border-[#FF2A5F]/30 text-[#FF2A5F] text-xs">{err}</div>}
      {rows.map(r => (
        <div key={r.category} className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid={`pricing-row-${r.category}`}>
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-[#FF7A00]"/>
            <div>
              <div className="font-outfit font-bold">{r.label}</div>
              <div className="text-[10px] text-white/50 uppercase tracking-wider">Category: {r.category}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] text-white/60 uppercase tracking-wider font-bold">Cost (Goldly)</label>
              <input type="number" value={r.cost} onChange={e => upd(r.category, "cost", e.target.value)} data-testid={`pricing-cost-${r.category}`}
                className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>
            </div>
            <div>
              <label className="text-[10px] text-white/60 uppercase tracking-wider font-bold">Duration (hours)</label>
              <input type="number" value={r.hours} onChange={e => upd(r.category, "hours", e.target.value)} data-testid={`pricing-hours-${r.category}`}
                className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>
            </div>
            <div>
              <label className="text-[10px] text-white/60 uppercase tracking-wider font-bold">Host share %</label>
              <input type="number" value={r.host_share_pct} onChange={e => upd(r.category, "host_share_pct", e.target.value)} data-testid={`pricing-share-${r.category}`}
                className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>
            </div>
          </div>
          <div className="mt-2 text-[10px] text-white/50">
            Split: Host gets <b className="text-emerald-300">{Math.round((r.cost || 0) * (r.host_share_pct || 70) / 100).toLocaleString()}</b> Tokwin · Platform gets <b className="text-[#FF7A00]">{Math.round((r.cost || 0) * (100 - (r.host_share_pct || 70)) / 100).toLocaleString()}</b> Goldly
          </div>
          <button onClick={() => save(r)} disabled={savingCat === r.category} data-testid={`pricing-save-${r.category}`}
            className="mt-3 px-4 py-2 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-outfit font-black text-xs flex items-center gap-1 disabled:opacity-50 active:scale-95">
            {savingCat === r.category ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
            Save
          </button>
        </div>
      ))}
    </div>
  );
}

const ARTIST_CATS = [
  { id: "artist",   label: "Artist" },
  { id: "actor",    label: "Actor" },
  { id: "actress",  label: "Actress" },
  { id: "model",    label: "Model" },
  { id: "sports",   label: "Sports Personalities" },
  { id: "chef",     label: "Chef" },
];

function ArtistsAdminPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // artist obj or {} for new
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => { setLoading(true); api.get("/admin/artists").then(r => setItems(r.data || [])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const newArtist = () => setEditing({ first_name: "", last_name: "", age: "", category: "artist", description: "", photos: [], videos: [], published: false });

  const save = async (publish = false) => {
    setErr(""); setSaving(true);
    try {
      const payload = { ...editing, published: publish, age: editing.age ? Number(editing.age) : null };
      if (editing.id) {
        const { data } = await api.put(`/admin/artists/${editing.id}`, payload);
        setItems(is => is.map(x => x.id === data.id ? data : x));
      } else {
        const { data } = await api.post("/admin/artists", payload);
        setItems(is => [data, ...is]);
      }
      setEditing(null);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this artist profile?")) return;
    await api.delete(`/admin/artists/${id}`);
    setItems(is => is.filter(x => x.id !== id));
  };

  const upd = (k, v) => setEditing(e => ({ ...e, [k]: v }));
  const wordCount = (editing?.description || "").trim().split(/\s+/).filter(Boolean).length;

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]"/></div>;

  if (editing) {
    return (
      <div className="space-y-3" data-testid="artist-editor">
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing(null)} className="p-2 rounded-full bg-white/8"><ArrowLeft className="w-4 h-4"/></button>
          <h3 className="font-outfit font-extrabold text-lg">{editing.id ? "Edit Artist" : "New Artist Profile"}</h3>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input value={editing.first_name} onChange={e => upd("first_name", e.target.value)} placeholder="First name" data-testid="artist-first-name" className="px-3 py-2.5 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#FF7A00]"/>
          <input value={editing.last_name} onChange={e => upd("last_name", e.target.value)} placeholder="Last name" data-testid="artist-last-name" className="px-3 py-2.5 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#FF7A00]"/>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" value={editing.age || ""} onChange={e => upd("age", e.target.value)} placeholder="Age" data-testid="artist-age" className="px-3 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>
          <select value={editing.category} onChange={e => upd("category", e.target.value)} data-testid="artist-category" className="px-3 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00] appearance-none">
            {ARTIST_CATS.map(c => <option key={c.id} value={c.id} className="bg-[#0B0F19]">{c.label}</option>)}
          </select>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold block mb-1">Photos ({(editing.photos || []).length}/10)</label>
          {(editing.photos || []).length > 0 && (
            <div className="grid grid-cols-5 gap-1.5 mb-2">
              {editing.photos.map((p, i) => (
                <div key={i} className="relative aspect-square">
                  <img src={p} alt="" className="w-full h-full object-cover rounded-lg"/>
                  <button onClick={() => upd("photos", editing.photos.filter((_, k) => k !== i))} className="absolute -top-1 -right-1 p-0.5 rounded-full bg-[#FF2A5F]"><X className="w-3 h-3"/></button>
                </div>
              ))}
            </div>
          )}
          {(editing.photos || []).length < 10 && (
            <CloudinaryUpload kind="image" maxMB={5} testId="artist-photo-upload"
              onUploaded={(d) => upd("photos", [...(editing.photos || []), d.secure_url])}/>
          )}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold block mb-1">Videos ({(editing.videos || []).length}/2)</label>
          {(editing.videos || []).length > 0 && (
            <div className="space-y-1.5 mb-2">
              {editing.videos.map((v, i) => (
                <div key={i} className="relative">
                  <video src={v} controls className="w-full max-h-40 rounded-lg"/>
                  <button onClick={() => upd("videos", editing.videos.filter((_, k) => k !== i))} className="absolute top-1 right-1 p-0.5 rounded-full bg-[#FF2A5F]"><X className="w-3 h-3"/></button>
                </div>
              ))}
            </div>
          )}
          {(editing.videos || []).length < 2 && (
            <CloudinaryUpload kind="video" maxMB={100} testId="artist-video-upload"
              onUploaded={(d) => upd("videos", [...(editing.videos || []), d.secure_url])}/>
          )}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold flex items-center justify-between mb-1">
            <span>Description</span>
            <span className={`text-[10px] normal-case ${wordCount <= 500 ? "text-emerald-400" : "text-[#FF2A5F]"}`}>{wordCount} / 500 words max</span>
          </label>
          <textarea rows={6} value={editing.description || ""} onChange={e => upd("description", e.target.value)} placeholder="Short description (max 500 words)" data-testid="artist-description" className="w-full px-3 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>
        </div>

        {err && <div className="text-[#FF2A5F] text-xs">{err}</div>}

        <div className="flex gap-2">
          <button onClick={() => setEditing(null)} className="px-4 py-3 rounded-full bg-white/8 border border-white/15 font-bold" data-testid="artist-cancel">Cancel</button>
          <button onClick={() => save(false)} disabled={saving} className="flex-1 py-3 rounded-full bg-white/10 border border-white/15 font-bold flex items-center justify-center gap-1 disabled:opacity-50" data-testid="artist-save-draft">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Save Draft
          </button>
          <button onClick={() => save(true)} disabled={saving || wordCount > 500} className="flex-1 py-3 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-outfit font-black flex items-center justify-center gap-1 disabled:opacity-40" data-testid="artist-publish">
            Publish
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-artists">
      <div className="flex items-center justify-between">
        <p className="text-white/70 text-xs">Create artist profiles (Actor, Model, Chef, etc.) that users can browse under Book Your Artist.</p>
        <button onClick={newArtist} className="px-4 py-2 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-outfit font-black text-xs flex items-center gap-1" data-testid="artist-new">
          <Plus className="w-3.5 h-3.5"/>New Artist
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-8 text-white/50 text-sm">No artist profiles yet. Tap New Artist to create one.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map(a => (
            <div key={a.id} className="p-3 rounded-2xl bg-white/5 border border-white/10 flex gap-3" data-testid={`artist-row-${a.id}`}>
              <div className="w-14 h-14 rounded-xl bg-white/5 overflow-hidden flex-shrink-0">
                {a.photos?.[0] ? <img src={a.photos[0]} alt="" className="w-full h-full object-cover"/> : <User className="w-full h-full text-white/25 p-3"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{a.first_name} {a.last_name}</div>
                <div className="text-[11px] text-white/60">{ARTIST_CATS.find(c => c.id === a.category)?.label}{a.age ? ` · ${a.age} yrs` : ""}</div>
                <div className="text-[10px] mt-1">
                  <span className={a.published ? "text-emerald-400" : "text-[#FF7A00]"}>{a.published ? "● Published" : "● Draft"}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => setEditing(a)} className="px-2 py-1 rounded-full bg-white/10 text-[10px] font-bold" data-testid={`artist-edit-${a.id}`}>Edit</button>
                <button onClick={() => del(a.id)} className="px-2 py-1 rounded-full bg-[#FF2A5F]/15 text-[#FF2A5F] text-[10px] font-bold" data-testid={`artist-del-${a.id}`}>Del</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function BiographyAdminLink() {
  const nav = useNavigate();
  return (
    <div className="text-center py-10" data-testid="admin-biography-link">
      <BookOpen className="w-14 h-14 mx-auto mb-2 text-[#FF7A00]"/>
      <h3 className="font-outfit font-extrabold text-xl mb-1">Biography Editor</h3>
      <p className="text-white/60 text-sm mb-4">Manage the Hifireels biography page (photos, videos, 1500+ word article).</p>
      <button onClick={() => nav("/biography")} data-testid="admin-open-biography"
        className="px-5 py-3 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-outfit font-extrabold inline-flex items-center gap-2 active:scale-95">
        <Edit3 className="w-4 h-4"/> Open Biography Editor
      </button>
    </div>
  );
}


function SoraUsageTile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/admin/sora-usage-today")
      .then(r => setData(r.data))
      .catch(e => setErr(formatErr(e)));
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  if (err) return <div className="mt-6 p-4 rounded-2xl bg-[#FF2A5F]/10 border border-[#FF2A5F]/30 text-[#FF2A5F] text-sm" data-testid="sora-usage-err">{err}</div>;
  const t = data?.totals || {};

  return (
    <div className="mt-6 rounded-2xl bg-gradient-to-br from-[#FF2A5F]/10 to-[#A855F7]/10 border border-[#A855F7]/30 p-5" data-testid="sora-usage-tile">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-[#A855F7]"/>
          <h3 className="font-outfit font-extrabold text-lg">Sora 2 usage (last 24h)</h3>
        </div>
        <button onClick={load} data-testid="sora-usage-refresh" className="p-1.5 rounded-full bg-white/8 border border-white/10 text-xs font-bold active:scale-95">Refresh</button>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { k: "jobs", label: "Jobs", color: "text-white" },
          { k: "completed", label: "Completed", color: "text-emerald-400" },
          { k: "seconds", label: "Seconds", color: "text-[#FF7A00]" },
          { k: "est_openai_cost_usd", label: "Est $ cost", color: "text-[#FF2A5F]" },
        ].map(s => (
          <div key={s.k} data-testid={`sora-total-${s.k}`} className="p-2.5 rounded-xl bg-black/40 border border-white/10">
            <div className={`font-outfit font-extrabold text-xl ${s.color}`}>
              {s.k === "est_openai_cost_usd" ? `$${(t[s.k] || 0).toFixed(2)}` : (t[s.k] ?? 0)}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-white/50">{s.label}</div>
          </div>
        ))}
      </div>
      {(data?.jobs || []).length === 0 ? (
        <div className="text-white/50 text-xs text-center py-3">No Sora 2 generations in the last 24h.</div>
      ) : (
        <div className="overflow-x-auto max-h-[360px]">
          <table className="w-full text-xs" data-testid="sora-usage-table">
            <thead className="text-[10px] uppercase text-white/50">
              <tr>
                <th className="text-left p-1.5">Email</th>
                <th className="text-left p-1.5">@username</th>
                <th className="text-right p-1.5">Sec</th>
                <th className="text-right p-1.5">Goldly</th>
                <th className="text-left p-1.5">Status</th>
                <th className="text-left p-1.5">When</th>
              </tr>
            </thead>
            <tbody>
              {data.jobs.map(j => (
                <tr key={j.job_id} data-testid={`sora-row-${j.job_id}`} className="border-t border-white/5 hover:bg-white/5">
                  <td className="p-1.5 truncate max-w-[180px]" title={j.email}>{j.email || <span className="text-white/40">—</span>}</td>
                  <td className="p-1.5">@{j.username || j.user_id?.slice(0, 8)}</td>
                  <td className="p-1.5 text-right text-[#FF7A00] font-bold">{j.duration_seconds}s</td>
                  <td className="p-1.5 text-right">{j.refunded ? <span className="text-emerald-400">refund</span> : j.cost_goldly}</td>
                  <td className="p-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      j.status === "completed" ? "bg-emerald-500/20 text-emerald-300" :
                      j.status === "failed"    ? "bg-[#FF2A5F]/20 text-[#FF2A5F]" :
                      "bg-white/10 text-white/60"
                    }`}>{j.status}</span>
                  </td>
                  <td className="p-1.5 text-white/60">{j.created_at ? new Date(j.created_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-3 text-[10px] text-white/45">Cost estimate: Sora 2 @ $0.10/second (720p). Debited from your Emergent Universal Key balance.</div>
    </div>
  );
}


function CreditWalletForm({ onCredited }) {
  const [mode, setMode] = useState("add"); // 'add' | 'remove'
  const [form, setForm] = useState({ identifier: "", currency: "goldly", amount: 1000, note: "Welcome bonus" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true); setResult(null);
    try {
      const id = form.identifier.trim();
      const raw = Math.abs(parseInt(form.amount) || 0);
      if (!raw) { setErr("Amount must be a positive number"); setBusy(false); return; }
      const amount = mode === "add" ? raw : -raw;
      const payload = { currency: form.currency, amount, note: form.note };
      if (id.includes("@")) payload.email = id;
      else if (id.length >= 30) payload.user_id = id;
      else payload.username = id;
      const { data } = await api.post("/admin/wallet/credit", payload);
      setResult(data);
      setForm(f => ({ ...f, identifier: "", amount: 1000, note: mode === "add" ? "Welcome bonus" : "Admin debit" }));
      onCredited?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBusy(false); }
  };

  const isAdd = mode === "add";
  const accent = isAdd ? "#FFD700" : "#FF2A5F";

  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#FF7A00]/10 to-[#00E5FF]/5 border border-[#FF7A00]/30 p-4" data-testid="admin-credit-form">
      <h2 className="font-outfit font-bold text-xl mb-1 flex items-center gap-2">
        <Plus className="w-5 h-5 text-[#FF7A00]"/>Wallet — Add or Remove
      </h2>
      <p className="text-xs text-white/60 mb-3">Choose Add or Remove, then enter the user's email / @username / user_id and amount.</p>

      {/* Add / Remove mode toggle */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button type="button" onClick={() => setMode("add")} data-testid="credit-mode-add"
          className={`py-2.5 rounded-xl font-bold text-sm border-2 transition active:scale-95 ${isAdd ? "bg-[#FF7A00] text-black border-[#FF7A00]" : "bg-white/5 border-white/15 text-white/70"}`}>
          ➕ Add (credit)
        </button>
        <button type="button" onClick={() => setMode("remove")} data-testid="credit-mode-remove"
          className={`py-2.5 rounded-xl font-bold text-sm border-2 transition active:scale-95 ${!isAdd ? "bg-[#FF2A5F] text-white border-[#FF2A5F]" : "bg-white/5 border-white/15 text-white/70"}`}>
          ➖ Remove (debit)
        </button>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="credit-wallet-form">
        <input data-testid="credit-identifier" required placeholder="email / @username / user_id" value={form.identifier} onChange={e=>setForm(f=>({...f, identifier:e.target.value}))} className="md:col-span-2 px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
        <select data-testid="credit-currency" value={form.currency} onChange={e=>setForm(f=>({...f, currency:e.target.value}))} className="px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none">
          <option value="goldly" className="bg-[#0B0F19]">🪙 Goldly</option>
          <option value="tokwin" className="bg-[#0B0F19]">💎 Tokwin</option>
        </select>
        <input data-testid="credit-amount" required type="number" min={1} placeholder="Amount" value={form.amount} onChange={e=>setForm(f=>({...f, amount:e.target.value}))} className="px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
        <button data-testid="credit-submit" disabled={busy} style={{ background: accent }} className="px-3 py-3 rounded-xl text-black font-bold disabled:opacity-50">{busy ? (isAdd ? "Adding…" : "Removing…") : (isAdd ? "Add" : "Remove")}</button>
        <input data-testid="credit-note" placeholder="Note (e.g., Welcome bonus / Promo / Refund / Penalty)" value={form.note} onChange={e=>setForm(f=>({...f, note:e.target.value}))} className="md:col-span-5 px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
      </form>
      {err && <div className="mt-2 text-[#FF2A5F] text-sm" data-testid="credit-err">{err}</div>}
      {result && (
        <div className="mt-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-sm" data-testid="credit-result">
          <b className="text-emerald-400">✓ Done</b> — {result.delta > 0 ? "credited" : "debited"} <b>{Math.abs(result.delta).toLocaleString()}</b> {result.currency}
          to <b>@{result.user.username || result.user.email}</b>. New balance: <b className="text-[#FF7A00]">{result.new_balance.toLocaleString()}</b>
        </div>
      )}
    </div>
  );
}

function DramaAdminPanel() {
  const [series, setSeries] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  // New episode form (one per open series)
  const [epForm, setEpForm] = useState({ ep_number: 1, title: "", description: "", video_url: "", cloudinary_public_id: null, duration_seconds: null, language: "en" });

  // Create-new-series form
  const [newSeries, setNewSeries] = useState({ title: "", description: "", cover_image: "", accent_color: "#FF2A5F", order: 0 });
  const [creating, setCreating] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);

  // Edit-poster state (per series)
  const [editingPosterId, setEditingPosterId] = useState(null);
  const [posterDraft, setPosterDraft] = useState("");

  const load = () => api.get("/admin/drama/series").then(r => setSeries(r.data));
  useEffect(() => { load(); }, []);

  const reloadEps = async (seriesId) => {
    const r = await api.get(`/drama/series/${seriesId}/episodes`);
    setSeries(s => s.map(x => x.id === seriesId ? { ...x, _eps: r.data, episode_count: r.data.length } : x));
  };

  const openSeries = async (s) => {
    if (openId === s.id) { setOpenId(null); return; }
    setOpenId(s.id);
    await reloadEps(s.id);
    setEpForm({ ep_number: (s.episode_count || 0) + 1, title: "", description: "", video_url: "", cloudinary_public_id: null, duration_seconds: null, language: "en" });
  };

  const submitEp = async (e, seriesId) => {
    e.preventDefault(); setMsg(""); setBusy(true);
    try {
      const payload = { series_id: seriesId, ...epForm };
      await api.post("/admin/drama/episodes", payload);
      setMsg(`✓ Episode ${epForm.ep_number} (${epForm.language.toUpperCase()}) uploaded!`);
      setEpForm(f => ({ ep_number: (f.ep_number || 0) + 1, title: "", description: "", video_url: "", cloudinary_public_id: null, duration_seconds: null, language: f.language }));
      reloadEps(seriesId);
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg(formatErr(e)); }
    finally { setBusy(false); }
  };

  const deleteEp = async (seriesId, epId) => {
    if (!window.confirm("Delete this episode?")) return;
    await api.delete(`/admin/drama/episodes/${epId}`);
    reloadEps(seriesId);
  };

  const createSeries = async (e) => {
    e?.preventDefault?.();
    if (!newSeries.title.trim()) { setMsg("Series title is required"); return; }
    setCreating(true); setMsg("");
    try {
      await api.post("/admin/drama/series", newSeries);
      setMsg("✓ Series created");
      setNewSeries({ title: "", description: "", cover_image: "", accent_color: "#FF2A5F", order: 0 });
      setShowNewForm(false);
      load();
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg(formatErr(e)); }
    finally { setCreating(false); }
  };

  const savePoster = async (seriesId) => {
    if (!posterDraft) return;
    try {
      await api.patch(`/admin/drama/series/${seriesId}`, { cover_image: posterDraft });
      setEditingPosterId(null);
      setPosterDraft("");
      load();
    } catch (e) { window.alert(formatErr(e)); }
  };

  const deleteSeries = async (s) => {
    if (!window.confirm(`Delete series "${s.title}" and all its episodes? Cannot be undone.`)) return;
    try {
      await api.delete(`/admin/drama/series/${s.id}`);
      load();
    } catch (e) { window.alert(formatErr(e)); }
  };

  const togglePremium = async (s) => {
    try {
      await api.patch(`/admin/drama/series/${s.id}`, { is_premium: !s.is_premium });
      load();
    } catch (e) { window.alert(formatErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="drama-admin-panel">
      <div className="rounded-2xl bg-gradient-to-br from-[#FF2A5F]/10 to-[#FF7A00]/5 border border-[#FF2A5F]/30 p-4">
        <h2 className="font-outfit font-bold text-xl mb-1 flex items-center gap-2"><Tv className="w-5 h-5 text-[#FF2A5F]"/>Vertical Drama Series</h2>
        <p className="text-xs text-white/60">Tap any series below to expand and upload episode videos. Episodes appear in order automatically.</p>
        <div className="mt-2 p-2.5 rounded-xl bg-[#FF7A00]/10 border border-[#FF7A00]/30 text-[11px] text-white/85">
          ⚠️ <b className="text-[#FF7A00]">IMPORTANT:</b> Upload Drama episodes ONLY from this form. Do NOT use the regular &quot;+&quot; Upload button in the bottom nav — that publishes to your personal reel feed, not the Drama series.
        </div>
        {msg && <div className="mt-2 text-sm text-emerald-300" data-testid="drama-msg">{msg}</div>}
      </div>

      {/* CREATE NEW SERIES */}
      {!showNewForm ? (
        <button onClick={() => setShowNewForm(true)} data-testid="drama-new-series-btn"
          className="w-full py-3 rounded-2xl bg-[#FF2A5F]/15 border border-[#FF2A5F]/40 text-[#FF2A5F] font-bold flex items-center justify-center gap-1.5 active:scale-95">
          <Plus className="w-4 h-4"/>Create New Drama Series
        </button>
      ) : (
        <form onSubmit={createSeries} className="rounded-2xl bg-[#FF2A5F]/8 border border-[#FF2A5F]/30 p-3 space-y-2" data-testid="drama-new-series-form">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-sm flex items-center gap-1.5"><Plus className="w-4 h-4 text-[#FF2A5F]"/>New Drama Series</h3>
            <button type="button" onClick={() => setShowNewForm(false)} data-testid="drama-new-cancel" className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4"/></button>
          </div>
          <ImageUploader
            label="Poster Image"
            value={newSeries.cover_image}
            onChange={(url) => setNewSeries(s => ({ ...s, cover_image: url }))}
            aspect="3/4"
            testId="drama-new-poster"
          />
          <input data-testid="drama-new-title" required value={newSeries.title} onChange={e => setNewSeries(s => ({ ...s, title: e.target.value }))} placeholder="Series title"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <textarea data-testid="drama-new-desc" rows={2} value={newSeries.description} onChange={e => setNewSeries(s => ({ ...s, description: e.target.value }))} placeholder="Short description"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Accent Color</label>
              <input data-testid="drama-new-color" type="color" value={newSeries.accent_color} onChange={e => setNewSeries(s => ({ ...s, accent_color: e.target.value }))}
                className="mt-1 w-full h-10 rounded-xl bg-white/5 border border-white/10 outline-none"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Display Order</label>
              <input data-testid="drama-new-order" type="number" value={newSeries.order} onChange={e => setNewSeries(s => ({ ...s, order: parseInt(e.target.value) || 0 }))}
                className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
            </div>
          </div>
          <button type="submit" disabled={creating} data-testid="drama-new-submit"
            className="w-full py-2.5 rounded-full bg-[#FF2A5F] text-white font-extrabold disabled:opacity-50 active:scale-95 flex items-center justify-center gap-1.5">
            <Plus className="w-4 h-4"/>{creating ? "Creating…" : "Create Series"}
          </button>
        </form>
      )}

      <div className="space-y-3">
        {series.map(s => (
          <div key={s.id} className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid={`series-${s.slug}`}>
            <div className="w-full flex items-center gap-3 p-3 text-left">
              <button onClick={() => openSeries(s)} className="w-14 h-20 rounded-lg overflow-hidden flex-shrink-0 active:scale-95"
                style={{ backgroundImage: s.cover_image ? `url(${s.cover_image})` : undefined, backgroundSize: "cover", backgroundPosition: "center", backgroundColor: s.accent_color + "33" }}
                data-testid={`series-poster-${s.slug}`} aria-label={`Open ${s.title}`}>
                {!s.cover_image && <ImageIcon className="w-5 h-5 text-white/40 mx-auto mt-7"/>}
              </button>
              <button onClick={() => openSeries(s)} className="flex-1 min-w-0 text-left active:scale-[0.99]">
                <div className="font-outfit font-bold text-base truncate" style={{ color: s.accent_color }}>{s.title}</div>
                <div className="text-[11px] text-white/60 truncate">{s.description}</div>
                <div className="text-[10px] text-white/50 mt-1">{s.episode_count || 0} episodes uploaded</div>
              </button>
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <button onClick={() => togglePremium(s)}
                  data-testid={`series-premium-${s.slug}`}
                  className={`px-2 py-1 rounded-md border text-[10px] font-bold flex items-center gap-1 active:scale-95 ${s.is_premium ? "bg-[#FF7A00]/25 border-[#FF7A00]/60 text-[#FF7A00]" : "bg-white/5 border-white/15 text-white/60"}`}>
                  🔒 {s.is_premium ? "Premium" : "Free"}
                </button>
                <button onClick={() => { setEditingPosterId(editingPosterId === s.id ? null : s.id); setPosterDraft(s.cover_image || ""); }}
                  data-testid={`series-edit-poster-${s.slug}`}
                  className="px-2 py-1 rounded-md bg-[#FF7A00]/15 border border-[#FF7A00]/40 text-[#FF7A00] text-[10px] font-bold flex items-center gap-1 active:scale-95">
                  <Edit3 className="w-3 h-3"/>Poster
                </button>
                <button onClick={() => deleteSeries(s)}
                  data-testid={`series-delete-${s.slug}`}
                  className="px-2 py-1 rounded-md bg-[#FF2A5F]/15 border border-[#FF2A5F]/40 text-[#FF2A5F] text-[10px] font-bold flex items-center gap-1 active:scale-95">
                  <Trash2 className="w-3 h-3"/>Del
                </button>
              </div>
            </div>

            {/* Inline Edit Poster panel */}
            {editingPosterId === s.id && (
              <div className="border-t border-white/10 p-3 bg-[#FF7A00]/5" data-testid={`series-edit-poster-panel-${s.slug}`}>
                <h4 className="font-bold text-sm mb-2 flex items-center gap-1.5"><ImageIcon className="w-4 h-4 text-[#FF7A00]"/>Update poster for &ldquo;{s.title}&rdquo;</h4>
                <ImageUploader value={posterDraft} onChange={setPosterDraft} aspect="3/4" testId={`series-poster-upload-${s.slug}`} label="Drama Poster"/>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button onClick={() => { setEditingPosterId(null); setPosterDraft(""); }}
                    data-testid={`series-poster-cancel-${s.slug}`}
                    className="py-2 rounded-full bg-white/5 border border-white/15 text-white text-xs font-bold active:scale-95">Cancel</button>
                  <button onClick={() => savePoster(s.id)} disabled={!posterDraft}
                    data-testid={`series-poster-save-${s.slug}`}
                    className="py-2 rounded-full bg-[#FF7A00] text-black text-xs font-extrabold disabled:opacity-50 active:scale-95 flex items-center justify-center gap-1.5">
                    <Save className="w-3.5 h-3.5"/>Save Poster
                  </button>
                </div>
              </div>
            )}

            {openId === s.id && (
              <div className="border-t border-white/10 p-3 space-y-3 bg-black/20" data-testid={`series-open-${s.slug}`}>
                <h3 className="font-bold text-sm">Upload New Episode</h3>
                <form onSubmit={(e) => submitEp(e, s.id)} className="space-y-2">
                  <div className="flex gap-2">
                    <input type="number" min={1} required placeholder="EP #" value={epForm.ep_number}
                      onChange={e => setEpForm(f => ({ ...f, ep_number: parseInt(e.target.value) || 1 }))}
                      data-testid="ep-number" className="w-20 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
                    <input required placeholder="Episode title" value={epForm.title}
                      onChange={e => setEpForm(f => ({ ...f, title: e.target.value }))}
                      data-testid="ep-title" className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-white/60 font-semibold block mb-1">Language</label>
                    <div className="grid grid-cols-3 gap-2" data-testid="ep-lang-picker">
                      {[
                        { code: "en", label: "English", color: "#00E5FF" },
                        { code: "hi", label: "हिन्दी Hindi", color: "#FFD700" },
                        { code: "gu", label: "ગુજરાતી Gujarati", color: "#A855F7" },
                      ].map(l => (
                        <button key={l.code} type="button" data-testid={`ep-lang-${l.code}`}
                          onClick={() => setEpForm(f => ({ ...f, language: l.code }))}
                          className={`p-2 rounded-xl border text-[11px] font-bold transition ${epForm.language === l.code ? "bg-white/10" : "bg-white/5 hover:bg-white/10"}`}
                          style={{ borderColor: epForm.language === l.code ? l.color : "rgba(255,255,255,0.1)", color: epForm.language === l.code ? l.color : "rgba(255,255,255,0.7)" }}>
                          {l.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <textarea rows={2} placeholder="Episode description (optional)" value={epForm.description}
                    onChange={e => setEpForm(f => ({ ...f, description: e.target.value }))}
                    data-testid="ep-desc" className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
                  <input placeholder="Episode title" required value={epForm.title}
                    onChange={e => setEpForm(f => ({ ...f, title: e.target.value }))}
                    data-testid="ep-title-full" className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
                  <VideoUploader
                    value={epForm.cloudinary_public_id ? { secure_url: epForm.video_url, public_id: epForm.cloudinary_public_id, duration: epForm.duration_seconds } : null}
                    onUploaded={(d) => setEpForm(f => ({
                      ...f,
                      video_url: d.secure_url,
                      cloudinary_public_id: d.public_id,
                      duration_seconds: d.duration ? Math.round(d.duration) : f.duration_seconds,
                    }))}
                    onCleared={() => setEpForm(f => ({ ...f, video_url: "", cloudinary_public_id: null, duration_seconds: null }))}
                    testId={`ep-video-uploader-${s.slug}`}
                    maxMB={500}
                    label="Episode video (from gallery)"
                  />
                  <button disabled={busy || !epForm.video_url} data-testid={`ep-submit-${s.slug}`}
                    className="w-full py-2.5 rounded-xl bg-[#00E5FF] text-black font-bold disabled:opacity-50">
                    {busy ? "Uploading…" : `+ Add Episode ${epForm.ep_number} (${epForm.language.toUpperCase()})`}
                  </button>
                </form>

                <h3 className="font-bold text-sm mt-4">Uploaded Episodes</h3>
                {(s._eps || []).length === 0 && <p className="text-xs text-white/40">No episodes yet — upload the first one above.</p>}
                <div className="space-y-1.5">
                  {(s._eps || []).map(ep => (
                    <div key={ep.id} className="flex items-center gap-2 p-2 rounded-xl bg-white/5">
                      {ep.thumbnail_url ? <img src={ep.thumbnail_url} alt="" className="w-10 h-12 rounded object-cover"/> : <div className="w-10 h-12 rounded bg-white/10 flex items-center justify-center"><Play className="w-3 h-3 text-white/40"/></div>}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold flex items-center gap-1.5">
                          <span>EP {ep.ep_number} • {ep.title}</span>
                          {ep.language && (
                            <span className="px-1.5 py-0 rounded-full text-[9px] font-extrabold tracking-wider"
                              style={{
                                background: `${({en:"#00E5FF",hi:"#FFD700",gu:"#A855F7"}[ep.language]||"#888")}22`,
                                color: ({en:"#00E5FF",hi:"#FFD700",gu:"#A855F7"}[ep.language]||"#aaa"),
                                border: `1px solid ${({en:"#00E5FF",hi:"#FFD700",gu:"#A855F7"}[ep.language]||"#888")}55`
                              }}>
                              {({en:"EN",hi:"HI",gu:"GU"}[ep.language] || ep.language.toUpperCase())}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-white/50">{ep.views || 0} views{ep.duration_seconds ? ` • ${Math.floor(ep.duration_seconds / 60)}:${String(ep.duration_seconds % 60).padStart(2,"0")}` : ""}</div>
                      </div>
                      <button onClick={() => deleteEp(s.id, ep.id)} className="text-[#FF2A5F]"><Trash2 className="w-4 h-4"/></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function PredictAdminPanel() {
  const [items, setItems] = useState([]);
  const [subs, setSubs] = useState([]);
  const [instr, setInstr] = useState("");
  const [form, setForm] = useState({ title: "", reward_text: "", reward_emoji: "🎁", video_url: "", image_url: "", min_goldly: 10 });
  const [editing, setEditing] = useState(null);
  const [instrSaved, setInstrSaved] = useState(false);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/admin/predict/all").then(r => r.data).catch(() => []),
      api.get("/admin/predict/submissions").then(r => r.data).catch(() => []),
      api.get("/predict/instructions").then(r => r.data).catch(() => ({ content: "" })),
    ]);
    setItems(a); setSubs(b); setInstr(c.content || "");
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, min_goldly: parseInt(form.min_goldly) || 0 };
      if (editing) {
        await api.put(`/admin/predict/${editing.id}`, payload);
      } else {
        await api.post("/predict/reveal", payload);
      }
      setEditing(null);
      setForm({ title: "", reward_text: "", reward_emoji: "🎁", video_url: "", image_url: "", min_goldly: 10 });
      load();
    } catch (e) { window.alert(formatErr(e)); }
  };
  const startEdit = (r) => { setEditing(r); setForm({ title: r.title || "", reward_text: r.reward_text || "", reward_emoji: r.reward_emoji || "🎁", video_url: r.video_url || "", image_url: r.image_url || "", min_goldly: r.min_goldly || 10 }); };
  const toggleHide = async (r) => { try { await api.put(`/admin/predict/${r.id}`, { is_hidden: !r.is_hidden }); load(); } catch (e) { window.alert(formatErr(e)); } };
  const remove = async (r) => { if (!window.confirm(`Delete "${r.title}"?`)) return; try { await api.delete(`/admin/predict/${r.id}`); load(); } catch (e) { window.alert(formatErr(e)); } };
  const saveInstr = async () => { try { await api.put("/admin/predict/instructions", { content: instr }); setInstrSaved(true); setTimeout(() => setInstrSaved(false), 1500); } catch (e) { window.alert(formatErr(e)); } };

  return (
    <>
      <form onSubmit={submit} className="p-5 rounded-2xl bg-white/5 border border-white/10 space-y-3" data-testid="predict-form">
        <h2 className="font-outfit font-bold text-xl mb-2">🎰 {editing ? "Edit Reveal" : "Reveal New Lucky Draw"}</h2>
        <input required placeholder="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="predict-title" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
        <input required placeholder="Reward text" value={form.reward_text} onChange={e => setForm(f => ({ ...f, reward_text: e.target.value }))} data-testid="predict-reward" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
        <input placeholder="Emoji 🎁" value={form.reward_emoji} onChange={e => setForm(f => ({ ...f, reward_emoji: e.target.value }))} data-testid="predict-emoji" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Minimum Goldly stake for users</label>
          <input type="number" min={0} placeholder="e.g. 10" value={form.min_goldly} onChange={e => setForm(f => ({ ...f, min_goldly: e.target.value }))} data-testid="predict-min-goldly" className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 outline-none"/>
        </div>
        <ImageUploader label="Photo (optional)" value={form.image_url} onChange={(url) => setForm(f => ({ ...f, image_url: url }))} aspect="16/9" testId="predict-image-upload"/>
        <VideoUploader label="Video (optional)" value={form.video_url ? { secure_url: form.video_url } : null}
          onUploaded={(d) => setForm(f => ({ ...f, video_url: d.secure_url }))}
          onCleared={() => setForm(f => ({ ...f, video_url: "" }))} testId="predict-video-upload" maxMB={500}/>
        <div className="flex gap-2">
          <button data-testid="predict-submit" className="flex-1 py-3 rounded-full bg-[#FF2A5F] text-white font-bold">{editing ? "Save Edit" : "Reveal to All Users"}</button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm({ title: "", reward_text: "", reward_emoji: "🎁", video_url: "", image_url: "", min_goldly: 10 }); }} className="px-4 py-3 rounded-full bg-white/10 font-bold text-sm">Cancel</button>}
        </div>

        {/* Instructions box */}
        <div className="pt-4 border-t border-white/10">
          <h3 className="text-sm font-bold text-white/80 mb-2">📋 Instructions box (shown to users on /predict)</h3>
          <textarea rows={4} value={instr} onChange={e => setInstr(e.target.value)} data-testid="predict-instr"
            placeholder="Type the rules / how-to for Predict &amp; Enjoy that users will see…"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <button type="button" onClick={saveInstr} data-testid="predict-instr-save" className="mt-2 px-4 py-2 rounded-full bg-[#A855F7] text-white font-bold text-xs">
            {instrSaved ? "✓ Saved" : "Save Instructions"}
          </button>
        </div>
      </form>

      <div className="space-y-4">
        <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
          <h2 className="font-outfit font-bold text-xl mb-3">Reveals ({items.length}) — Edit / Hide / Delete</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {items.map(r => (
              <div key={r.id} data-testid={`predict-row-${r.id}`} className={`flex items-center gap-2 p-2 rounded-xl bg-white/5 ${r.is_hidden ? "opacity-50" : ""}`}>
                <span className="text-2xl">{r.reward_emoji}</span>
                <div className="flex-1 min-w-0 text-sm">
                  <div className="font-bold truncate">{r.title}</div>
                  <div className="text-[10px] text-white/50">{r.reward_text}</div>
                </div>
                <button onClick={() => startEdit(r)} data-testid={`predict-edit-${r.id}`} className="px-2 py-1 rounded bg-[#00E5FF]/20 text-[#00E5FF] text-[10px] font-bold">✎ Edit</button>
                <button onClick={() => toggleHide(r)} data-testid={`predict-hide-${r.id}`} className={`px-2 py-1 rounded text-[10px] font-bold ${r.is_hidden ? "bg-[#FF7A00]/20 text-[#FF7A00]" : "bg-white/10 text-white/60"}`}>{r.is_hidden ? "🙈 Hidden" : "👁 Hide"}</button>
                <button onClick={() => remove(r)} data-testid={`predict-del-${r.id}`} className="px-2 py-1 rounded bg-[#FF2A5F]/20 text-[#FF2A5F] text-[10px] font-bold">🗑</button>
              </div>
            ))}
            {items.length === 0 && <p className="text-white/40 text-sm text-center py-4">No reveals yet.</p>}
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
          <h2 className="font-outfit font-bold text-xl mb-3">User Predictions ({subs.length})</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto text-xs">
            {subs.map(s => (
              <div key={s.id} data-testid={`predict-sub-${s.id}`} className="p-2 rounded-xl bg-white/5">
                <div className="flex items-center justify-between">
                  <span className="font-bold">@{s.username} · {s.email || "—"}</span>
                  <span className="text-[#FF7A00] font-bold">🪙 {s.goldly_amount}</span>
                </div>
                <div className="text-white/60 mt-1">{s.prediction || "(no message)"}</div>
                <div className="text-[10px] text-white/40">{new Date(s.created_at).toLocaleString()}</div>
              </div>
            ))}
            {subs.length === 0 && <p className="text-white/40 text-sm text-center py-4">No predictions yet.</p>}
          </div>
        </div>
      </div>
    </>
  );
}

function LeadGenConfigPanel() {
  const [cfg, setCfg] = useState({ categories: [], cities: [], cover: {} });
  const [newCat, setNewCat] = useState("");
  const [newCity, setNewCity] = useState("");
  const [cover, setCover] = useState({ headline: "", price_goldly: 0, price_inr: 0, subtext: "", image_url: "", active: true });
  const [savingCover, setSavingCover] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/lead-gen/config");
    setCfg({ categories: data.categories || [], cities: data.cities || [], cover: data.cover || {} });
    setCover({
      headline: data.cover?.headline || "",
      price_goldly: data.cover?.price_goldly || 0,
      price_inr: data.cover?.price_inr || 0,
      subtext: data.cover?.subtext || "",
      image_url: data.cover?.image_url || "",
      active: data.cover?.active ?? true,
    });
  };
  useEffect(() => { load(); }, []);

  const addCat = async () => {
    const name = newCat.trim(); if (!name) return;
    try { await api.post("/admin/lead-gen/categories", { name }); setNewCat(""); load(); }
    catch (e) { window.alert(formatErr(e)); }
  };
  const delCat = async (name) => {
    if (!window.confirm(`Remove category "${name}"?`)) return;
    try { await api.delete(`/admin/lead-gen/categories/${encodeURIComponent(name)}`); load(); }
    catch (e) { window.alert(formatErr(e)); }
  };
  const addCity = async () => {
    const name = newCity.trim(); if (!name) return;
    try { await api.post("/admin/lead-gen/cities", { name }); setNewCity(""); load(); }
    catch (e) { window.alert(formatErr(e)); }
  };
  const delCity = async (name) => {
    if (!window.confirm(`Remove city "${name}"?`)) return;
    try { await api.delete(`/admin/lead-gen/cities/${encodeURIComponent(name)}`); load(); }
    catch (e) { window.alert(formatErr(e)); }
  };
  const saveCover = async (e) => {
    e.preventDefault();
    setSavingCover(true); setMsg("");
    try {
      await api.put("/admin/lead-gen/cover", {
        ...cover,
        price_goldly: parseInt(cover.price_goldly) || 0,
        price_inr: parseInt(cover.price_inr) || 0,
      });
      setMsg("✓ Cover saved");
      setTimeout(() => setMsg(""), 1500);
      load();
    } catch (e) { window.alert(formatErr(e)); }
    finally { setSavingCover(false); }
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#A855F7]/10 to-[#FF7A00]/5 border border-[#A855F7]/30 p-4 space-y-4" data-testid="leadgen-config-panel">
      <div>
        <h2 className="font-outfit font-bold text-xl flex items-center gap-2"><Users className="w-5 h-5 text-[#A855F7]"/>Lead Generation — Config</h2>
        <p className="text-xs text-white/60 mt-1">Edit the launch-offer cover, manage categories &amp; cities shown to users.</p>
      </div>

      {/* Cover editor */}
      <form onSubmit={saveCover} className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="leadgen-cover-form">
        <h3 className="text-xs uppercase tracking-wider text-white/60 font-bold">Cover banner (shown on /lead-gen)</h3>
        <input data-testid="leadgen-cover-headline" required placeholder="Headline (e.g. First 100 verified leads)" value={cover.headline} onChange={e => setCover(c => ({ ...c, headline: e.target.value }))}
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="leadgen-cover-goldly" type="number" min={0} placeholder="Price in Goldly" value={cover.price_goldly} onChange={e => setCover(c => ({ ...c, price_goldly: e.target.value }))}
            className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <input data-testid="leadgen-cover-inr" type="number" min={0} placeholder="Price in ₹ (INR)" value={cover.price_inr} onChange={e => setCover(c => ({ ...c, price_inr: e.target.value }))}
            className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        </div>
        <textarea data-testid="leadgen-cover-subtext" rows={2} placeholder="Sub-text under the price" value={cover.subtext} onChange={e => setCover(c => ({ ...c, subtext: e.target.value }))}
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        <input data-testid="leadgen-cover-image" placeholder="Cover image URL (optional)" value={cover.image_url} onChange={e => setCover(c => ({ ...c, image_url: e.target.value }))}
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        <label className="flex items-center gap-2 text-xs text-white/70">
          <input type="checkbox" checked={cover.active} onChange={e => setCover(c => ({ ...c, active: e.target.checked }))} data-testid="leadgen-cover-active"/>
          Show this banner on /lead-gen
        </label>
        <button disabled={savingCover} data-testid="leadgen-cover-save"
          className="w-full py-2.5 rounded-full bg-[#A855F7] text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50">
          {savingCover ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
          Save Cover
        </button>
        {msg && <div className="text-emerald-300 text-xs text-center">{msg}</div>}
      </form>

      {/* Categories */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
        <h3 className="text-xs uppercase tracking-wider text-white/60 font-bold mb-2">Categories ({cfg.categories.length})</h3>
        <div className="flex gap-2 mb-2">
          <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Add a category" data-testid="leadgen-new-category"
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCat(); } }}
            className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <button onClick={addCat} data-testid="leadgen-add-category" className="px-3 py-2 rounded-xl bg-[#A855F7] text-white font-bold text-xs active:scale-95">+ Add</button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {cfg.categories.map(c => (
            <span key={c} data-testid={`leadgen-cat-${c}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/8 border border-white/10 text-xs">
              {c}
              <button onClick={() => delCat(c)} data-testid={`leadgen-del-cat-${c}`} className="text-[#FF2A5F] hover:opacity-80"><Trash2 className="w-3 h-3"/></button>
            </span>
          ))}
          {cfg.categories.length === 0 && <span className="text-xs text-white/40">No categories yet.</span>}
        </div>
      </div>

      {/* Cities */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
        <h3 className="text-xs uppercase tracking-wider text-white/60 font-bold mb-2">Cities ({cfg.cities.length})</h3>
        <div className="flex gap-2 mb-2">
          <input value={newCity} onChange={e => setNewCity(e.target.value)} placeholder="Add a city" data-testid="leadgen-new-city"
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCity(); } }}
            className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <button onClick={addCity} data-testid="leadgen-add-city" className="px-3 py-2 rounded-xl bg-[#A855F7] text-white font-bold text-xs active:scale-95">+ Add</button>
        </div>
        <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto">
          {cfg.cities.map(c => (
            <span key={c} data-testid={`leadgen-city-${c}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/8 border border-white/10 text-xs">
              {c}
              <button onClick={() => delCity(c)} data-testid={`leadgen-del-city-${c}`} className="text-[#FF2A5F] hover:opacity-80"><Trash2 className="w-3 h-3"/></button>
            </span>
          ))}
          {cfg.cities.length === 0 && <span className="text-xs text-white/40">No cities yet.</span>}
        </div>
      </div>
    </div>
  );
}

function LeadsAdminPanel() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editingLead, setEditingLead] = useState(null);
  const [editForm, setEditForm] = useState({});
  const load = () => api.get("/admin/lead-requests").then(r => setLeads(r.data || []));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/lead-requests/${id}?status=${status}`);
      load();
    } catch (e) { window.alert(formatErr(e)); }
  };

  const startEdit = (l) => {
    setEditingLead(l);
    setEditForm({
      name: l.name || "", phone: l.phone || "", email: l.email || "",
      category: l.category || "", city: l.city || "",
      business_name: l.business_name || "", notes: l.notes || "",
    });
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/lead-requests/${editingLead.id}`, editForm);
      setEditingLead(null);
      load();
    } catch (e) { window.alert(formatErr(e)); }
  };
  const delLead = async (id) => {
    if (!window.confirm("Delete this lead request permanently?")) return;
    try {
      await api.delete(`/admin/lead-requests/${id}`);
      load();
    } catch (e) { window.alert(formatErr(e)); }
  };

  const filtered = filter === "all" ? leads : leads.filter(l => l.status === filter);

  return (
    <div className="space-y-3" data-testid="leads-admin-panel">
      <div className="rounded-2xl bg-gradient-to-br from-[#FF7A00]/10 to-[#FF7A00]/5 border border-[#FF7A00]/30 p-4">
        <h2 className="font-outfit font-bold text-xl mb-1 flex items-center gap-2"><Users className="w-5 h-5 text-[#FF7A00]"/>Lead Generation Requests</h2>
        <p className="text-xs text-white/60">Users who signed up for city-wise business leads. Match them with leads, then mark status.</p>
      </div>

      <div className="flex gap-2 overflow-x-auto">
        {["all", "pending", "matched", "closed"].map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`leads-filter-${f}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border ${filter === f ? "bg-[#FF7A00] text-black border-[#FF7A00]" : "bg-white/5 text-white/70 border-white/10"}`}>
            {f.toUpperCase()} ({f === "all" ? leads.length : leads.filter(l => l.status === f).length})
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.map(l => (
          <div key={l.id} className="rounded-2xl bg-white/5 border border-white/10 p-3" data-testid={`lead-${l.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm">{l.name}</span>
                  <span className="text-[10px] text-white/50">@{l.user_username}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-extrabold ${
                    l.status === "matched" ? "bg-emerald-500/20 text-emerald-300" :
                    l.status === "closed" ? "bg-white/10 text-white/50" :
                    "bg-[#FF7A00]/15 text-[#FF7A00]"
                  }`}>{l.status.toUpperCase()}</span>
                </div>
                <div className="text-[11px] text-white/70 mt-1">📞 {l.phone} {l.email ? ` • ✉ ${l.email}` : ""}</div>
                {l.business_name && <div className="text-[11px] text-white/60">🏢 {l.business_name}</div>}
              </div>
              <div className="text-right text-[10px] text-white/40">{new Date(l.created_at).toLocaleDateString()}</div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 rounded-full bg-[#FF7A00]/15 text-[#FF7A00] text-[10px] font-bold">{l.category}</span>
              <span className="px-2 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] text-[10px] font-bold">📍 {l.city}</span>
            </div>
            {l.notes && <div className="text-[11px] text-white/65 italic mt-1 mb-2 line-clamp-3">"{l.notes}"</div>}
            <div className="flex gap-2 mt-2">
              <button onClick={() => updateStatus(l.id, "matched")} disabled={l.status === "matched"}
                className="text-[11px] px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-bold disabled:opacity-40">
                ✓ Matched
              </button>
              <button onClick={() => updateStatus(l.id, "pending")} disabled={l.status === "pending"}
                className="text-[11px] px-3 py-1.5 rounded-full bg-[#FF7A00]/15 text-[#FF7A00] border border-[#FF7A00]/30 font-bold disabled:opacity-40">
                ⏳ Pending
              </button>
              <button onClick={() => updateStatus(l.id, "closed")} disabled={l.status === "closed"}
                className="text-[11px] px-3 py-1.5 rounded-full bg-white/10 text-white/70 border border-white/15 font-bold disabled:opacity-40">
                ✕ Close
              </button>
              <button onClick={() => startEdit(l)} data-testid={`lead-edit-${l.id}`}
                className="text-[11px] px-3 py-1.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/30 font-bold">
                ✎ Edit
              </button>
              <button onClick={() => delLead(l.id)} data-testid={`lead-delete-${l.id}`}
                className="text-[11px] px-3 py-1.5 rounded-full bg-[#FF2A5F]/15 text-[#FF2A5F] border border-[#FF2A5F]/30 font-bold">
                🗑 Delete
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center text-white/40 text-sm py-8">No lead requests in this filter.</p>}
      </div>

      {/* Edit modal */}
      {editingLead && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={() => setEditingLead(null)} data-testid="lead-edit-modal">
          <div onClick={e => e.stopPropagation()} className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-3xl bg-[#0B0F19] border border-white/15 p-5">
            <h3 className="font-outfit font-bold text-xl mb-3">Edit Lead Request</h3>
            <div className="space-y-2">
              {[
                ["name", "Name", "text"],
                ["phone", "Phone", "tel"],
                ["email", "Email", "email"],
                ["category", "Category", "text"],
                ["city", "City", "text"],
                ["business_name", "Business name", "text"],
              ].map(([k, label, type]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{label}</label>
                  <input data-testid={`lead-edit-${k}`} type={type} value={editForm[k]} onChange={e => setEditForm(f => ({ ...f, [k]: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
                </div>
              ))}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Notes</label>
                <textarea data-testid="lead-edit-notes" rows={3} value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditingLead(null)} className="flex-1 py-2.5 rounded-full bg-white/10 font-bold text-sm">Cancel</button>
              <button onClick={saveEdit} data-testid="lead-edit-save" className="flex-1 py-2.5 rounded-full bg-[#00E5FF] text-black font-bold text-sm">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BroadcastAdminPanel({ setMsg }) {
  const [form, setForm] = useState({ title: "", body: "", link: "", icon: "📢", only_active_days: "" });
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);

  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {
        title: form.title, body: form.body, link: form.link, icon: form.icon || "📢",
        only_active_days: form.only_active_days ? parseInt(form.only_active_days) : null,
      };
      const { data } = await api.post("/admin/notifications/broadcast", payload);
      setMsg && setMsg(`📢 Broadcast sent to ${data.sent_to} users`);
      setRecent(r => [{ ...payload, sent_to: data.sent_to, at: new Date().toLocaleString() }, ...r]);
      setForm({ title: "", body: "", link: "", icon: "📢", only_active_days: "" });
      setTimeout(() => setMsg && setMsg(""), 4000);
    } catch (e) { window.alert(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="broadcast-admin-panel">
      <div className="rounded-2xl bg-gradient-to-br from-[#00E5FF]/10 to-[#FF2A5F]/5 border border-[#00E5FF]/30 p-4">
        <h2 className="font-outfit font-bold text-xl mb-1 flex items-center gap-2"><Bell className="w-5 h-5 text-[#00E5FF]"/>Broadcast Notification</h2>
        <p className="text-xs text-white/60">Send an in-app notification to all users (or only recently-joined). Use for announcements, rewards, opportunities, drama releases.</p>
      </div>
      <form onSubmit={send} className="space-y-2 p-4 rounded-2xl bg-white/5 border border-white/10">
        <div className="flex gap-2">
          <input value={form.icon} maxLength={2} onChange={e => setForm(f => ({...f, icon: e.target.value}))}
            placeholder="📢" data-testid="bcast-icon"
            className="w-16 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-center text-xl"/>
          <input required value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}
            placeholder="Title (e.g. 🎬 Episode 5 just dropped!)" data-testid="bcast-title"
            className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 outline-none"/>
        </div>
        <textarea required rows={3} value={form.body} onChange={e => setForm(f => ({...f, body: e.target.value}))}
          placeholder="Body text — keep it short and exciting" data-testid="bcast-body"
          className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 outline-none"/>
        <input value={form.link} onChange={e => setForm(f => ({...f, link: e.target.value}))}
          placeholder="Link (optional) e.g. /drama/fashion or /earn-goldly" data-testid="bcast-link"
          className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 outline-none"/>
        <div>
          <label className="text-[10px] uppercase text-white/60 font-bold tracking-wider">Audience</label>
          <select value={form.only_active_days} onChange={e => setForm(f => ({...f, only_active_days: e.target.value}))}
            data-testid="bcast-audience"
            className="w-full mt-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 outline-none">
            <option value="" className="bg-[#0B0F19]">All users</option>
            <option value="1" className="bg-[#0B0F19]">Users active in last 1 day</option>
            <option value="7" className="bg-[#0B0F19]">Users active in last 7 days</option>
            <option value="30" className="bg-[#0B0F19]">Users active in last 30 days</option>
          </select>
        </div>
        <button disabled={busy || !form.title || !form.body} data-testid="bcast-send"
          className="w-full py-2.5 rounded-xl bg-[#00E5FF] text-black font-bold disabled:opacity-50">
          {busy ? "Sending..." : "📢 Send Broadcast"}
        </button>
      </form>
      {recent.length > 0 && (
        <div>
          <h3 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2">Recent Broadcasts (this session)</h3>
          <div className="space-y-2">
            {recent.map((b, i) => (
              <div key={i} className="p-3 rounded-2xl bg-white/5 border border-white/10">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-sm">{b.icon} {b.title}</div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 font-bold">Sent to {b.sent_to}</span>
                </div>
                <p className="text-xs text-white/70 mt-1">{b.body}</p>
                <p className="text-[10px] text-white/40 mt-1">{b.at}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function AdminCreateUserForm({ onCreated }) {
  const blank = { email: "", password: "", name: "", username: "", phone: "", country_code: "+91", role: "user", wallet_goldly: 0, primary_content: "reels", language: "en", bio: "" };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [created, setCreated] = useState([]); // session log

  const genPassword = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let s = "";
    for (let i = 0; i < 10; i++) s += chars[Math.floor(Math.random() * chars.length)];
    setForm(f => ({ ...f, password: s }));
  };

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setBusy(true);
    try {
      const payload = { ...form, wallet_goldly: parseInt(form.wallet_goldly) || 0 };
      const { data } = await api.post("/admin/users/create", payload);
      const u = data.user;
      setCreated(arr => [{ email: u.email, username: u.username, password: form.password, name: u.name, role: u.role, at: new Date().toLocaleString() }, ...arr]);
      setMsg(`✓ Created @${u.username} • Goldly ${u.wallet_goldly} • role ${u.role}`);
      setForm({ ...blank });
      onCreated && onCreated();
      setTimeout(() => setMsg(""), 4000);
    } catch (e) {
      setMsg(formatErr(e) || "Failed to create user");
    } finally { setBusy(false); }
  };

  const copyCreds = (c) => {
    const txt = `Hifireels account created!\nEmail: ${c.email}\nPassword: ${c.password}\nUsername: @${c.username}\nLogin at: https://hifireels.com/login`;
    navigator.clipboard?.writeText(txt).then(() => setMsg("✓ Credentials copied to clipboard"));
    setTimeout(() => setMsg(""), 2500);
  };

  return (
    <div className="rounded-2xl border border-[#FF7A00]/30 bg-gradient-to-br from-[#FF7A00]/10 to-transparent p-4" data-testid="admin-create-user">
      <div className="flex items-center gap-2 mb-1">
        <UserPlus className="w-5 h-5 text-[#FF7A00]"/>
        <h2 className="font-outfit font-bold text-lg">Admin: Create User Account</h2>
      </div>
      <p className="text-xs text-white/60 mb-3">Create accounts directly without email verification. Use this for bulk onboarding, agency creators, premium uploaders, etc.</p>

      <form onSubmit={submit} className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <input required value={form.name} onChange={upd("name")} placeholder="Full name *" data-testid="cu-name" className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <input value={form.username} onChange={upd("username")} placeholder="Username (auto if blank)" data-testid="cu-username" className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        </div>
        <input type="email" value={form.email} onChange={upd("email")} placeholder="Email (optional — leave blank to skip verification)" data-testid="cu-email"
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        <p className="text-[10px] text-white/40 -mt-1">Real users need email OTP. Admin-created accounts can skip — a placeholder is auto-generated and you can add the real email later via Edit.</p>
        <div className="flex gap-2">
          <input value={form.country_code} onChange={upd("country_code")} placeholder="+91" data-testid="cu-cc" className="w-20 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm text-center"/>
          <input value={form.phone} onChange={(e)=>setForm(f=>({...f, phone:e.target.value.replace(/\D/g,"")}))} placeholder="Phone (optional)" inputMode="numeric" data-testid="cu-phone"
            className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        </div>
        <div className="flex gap-2">
          <input required value={form.password} onChange={upd("password")} placeholder="Password (min 6) *" data-testid="cu-password"
            className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm font-mono"/>
          <button type="button" onClick={genPassword} data-testid="cu-gen-password"
            className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-[11px] font-bold">Generate</button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <input type="number" min={0} value={form.wallet_goldly} onChange={upd("wallet_goldly")} placeholder="Initial Goldly" data-testid="cu-goldly"
            className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <select value={form.role} onChange={upd("role")} data-testid="cu-role" className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm">
            <option value="user" className="bg-[#0B0F19]">User</option>
            <option value="admin" className="bg-[#0B0F19]">Admin</option>
          </select>
          <select value={form.primary_content} onChange={upd("primary_content")} data-testid="cu-primary" className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm">
            <option value="reels" className="bg-[#0B0F19]">Reels</option>
            <option value="live" className="bg-[#0B0F19]">Live</option>
          </select>
        </div>
        <textarea rows={2} value={form.bio} onChange={upd("bio")} placeholder="Bio (optional)" data-testid="cu-bio"
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
        <button disabled={busy} data-testid="cu-submit"
          className="w-full py-2.5 rounded-xl bg-[#FF7A00] text-black font-bold disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? "Creating..." : <><UserPlus className="w-4 h-4"/> Create Account (No Email Verification)</>}
        </button>
        {msg && <div data-testid="cu-msg" className={`text-sm text-center py-1.5 rounded-lg ${msg.startsWith("✓") ? "text-emerald-300 bg-emerald-500/10" : "text-[#FF2A5F] bg-[#FF2A5F]/10"}`}>{msg}</div>}
      </form>

      {created.length > 0 && (
        <div className="mt-4">
          <h3 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2">Recently created (this session)</h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {created.map((c, i) => (
              <div key={i} className="p-2.5 rounded-xl bg-white/5 border border-white/10" data-testid={`created-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-bold truncate">{c.name} <span className="text-white/50 text-[10px]">@{c.username}</span></div>
                    <div className="text-[11px] text-white/70 font-mono">{c.email}</div>
                    <div className="text-[11px] text-[#FF7A00] font-mono">PW: {c.password}</div>
                  </div>
                  <button type="button" onClick={() => copyCreds(c)} data-testid={`copy-creds-${i}`}
                    className="px-2 py-1.5 rounded-lg bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] text-[10px] font-bold flex items-center gap-1">
                    <Copy className="w-3 h-3"/>Copy
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-white/40 mt-2">💡 Copy &amp; share these credentials with the user via DM/WhatsApp. Encourage them to change their password after first login.</p>
        </div>
      )}
    </div>
  );
}



function SeedCreatorsPanel({ onSeeded }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const run = async () => {
    setErr(""); setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/seed-creators", {});
      setResult(data);
      onSeeded?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#00E5FF]/30 bg-gradient-to-br from-[#00E5FF]/10 to-transparent p-4" data-testid="seed-creators-panel">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-5 h-5 text-[#00E5FF]"/>
        <h2 className="font-outfit font-bold text-lg">Seed 200 Growth Creator Accounts</h2>
      </div>
      <p className="text-xs text-white/60 mb-3">
        Idempotently creates <b>creator001</b> &rarr; <b>creator200</b> (password <span className="font-mono text-[#FF7A00]">Hifireels@2026</span>) on the <b>current</b> database. Safe to run on production after a fresh deploy.
      </p>
      <button onClick={run} disabled={busy} data-testid="seed-creators-btn"
        className="w-full py-2.5 rounded-xl bg-[#00E5FF] text-black font-bold disabled:opacity-50 flex items-center justify-center gap-2">
        {busy ? "Seeding..." : <><Users className="w-4 h-4"/> Activate / Top-up 200 Creator Accounts</>}
      </button>
      {result && (
        <div data-testid="seed-creators-result" className="mt-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-xs">
          <div>✓ <b>{result.total_seed_creators}</b>/200 creator accounts active on this database</div>
          <div className="text-white/60 mt-1">Newly created this run: <b className="text-[#00E5FF]">{result.newly_created}</b> &middot; Existed before: <b>{result.before}</b></div>
          <a href="/Hifireels_200_Creator_Accounts.pdf" target="_blank" rel="noopener noreferrer"
             className="inline-block mt-2 text-[#FF7A00] underline">📄 Download credentials PDF</a>
        </div>
      )}
      {err && <div data-testid="seed-creators-err" className="mt-3 text-sm text-[#FF2A5F] bg-[#FF2A5F]/10 p-2 rounded-lg">{err}</div>}
    </div>
  );
}

function AdminReportRow({ r, onChange }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); onChange(); } catch (e) { alert(e?.response?.data?.detail || "Action failed"); } finally { setBusy(false); } };
  const setStatus = (status) => run(() => api.patch(`/admin/reports/${r.id}`, { status }));
  const toggleBan = () => {
    const banned = r.target?.banned;
    if (!window.confirm(banned ? `Unban @${r.target?.username}?` : `Ban @${r.target?.username}? They will be logged out and unable to log in.`)) return;
    run(() => api.post(`/admin/users/${r.target_id}/ban`));
  };
  const colors = { pending: "#FF7A00", reviewed: "#22C55E", dismissed: "#94A3B8" };
  const st = r.status || "pending";
  return (
    <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-sm" data-testid={`report-row-${r.id}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-bold text-[#FF2A5F]">{r.reason}</span>
        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ color: colors[st], background: colors[st] + "22" }} data-testid={`report-status-${r.id}`}>{st}</span>
      </div>
      {r.note && <p className="text-xs text-white/70 mb-1">"{r.note}"</p>}
      <div className="text-[11px] text-white/60">
        Reported by <a href={`/u/${r.reporter_id}`} className="text-[#00E5FF]">@{r.reporter?.username || r.reporter_id}</a>
        {" → "}<a href={`/u/${r.target_id}`} className="text-[#FF2A5F]" data-testid={`report-target-${r.id}`}>@{r.target?.username || r.target_id}</a>
        {r.target?.banned && <span className="ml-1 text-[#FF2A5F] font-bold">(BANNED)</span>}
      </div>
      <div className="text-[10px] text-white/40 mb-2">{new Date(r.created_at).toLocaleString()}</div>
      <div className="flex flex-wrap gap-2">
        {st !== "reviewed" && <button disabled={busy} onClick={()=>setStatus("reviewed")} data-testid={`report-reviewed-${r.id}`} className="px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-bold">Mark Reviewed</button>}
        {st !== "dismissed" && <button disabled={busy} onClick={()=>setStatus("dismissed")} data-testid={`report-dismiss-${r.id}`} className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-bold">Dismiss</button>}
        {st !== "pending" && <button disabled={busy} onClick={()=>setStatus("pending")} data-testid={`report-reopen-${r.id}`} className="px-3 py-1 rounded-full bg-[#FF7A00]/15 border border-[#FF7A00]/40 text-[#FF7A00] text-xs font-bold">Reopen</button>}
        <button disabled={busy} onClick={toggleBan} data-testid={`report-ban-${r.id}`} className="px-3 py-1 rounded-full bg-[#FF2A5F]/15 border border-[#FF2A5F]/40 text-[#FF2A5F] text-xs font-bold">{r.target?.banned ? "Unban User" : "Ban User"}</button>
      </div>
    </div>
  );
}
